<template>
  <header
    class="base-header"
    :class="{
      'is-mobile': isMobile,
      'is-menu-open': isMenuOpen,
    }"
  >
    <template v-if="type !== 'simple'">
      <!-- 主导航栏 -->
      <div class="base-header__navbar">
        <div class="base-container">
          <!-- PC 端：Logo + 导航菜单 + 操作区 -->
          <div class="base-header__navbar-pc">
            <div class="base-header__navbar-pc-inner">
              <div class="base-header__logo">
                <NuxtLink :to="logoTo">
                  <img
                    :src="withBase('/images/brand/logo-primary.svg')"
                    alt="品牌标识"
                    class="logo-img"
                    width="84"
                    height="24"
                  />
                </NuxtLink>
              </div>
              <div v-if="showNav" class="base-header__nav-wrapper">
                <div
                  v-if="isHeaderNavLoading"
                  class="base-header__menu base-header__menu--skeleton"
                  aria-hidden="true"
                >
                  <a-skeleton
                    v-for="item in headerNavSkeletonItems"
                    :key="item.key"
                    class="base-header__menu-skeleton-item"
                    animation
                  >
                    <a-skeleton-line :rows="1" :widths="[item.desktopWidth]" />
                  </a-skeleton>
                </div>
                <nav
                  v-else
                  class="base-header__menu"
                  aria-label="主导航"
                  @wheel="handleCategoryNavWheel"
                >
                  <div
                    v-if="showHotProductsNav"
                    class="base-header__menu-item-wrapper"
                    data-menu-key="store"
                    @mouseenter="handleCategoryNavMouseEnter('store')"
                    @mouseleave="handleCategoryNavTriggerMouseLeave('store', $event)"
                  >
                    <button
                      type="button"
                      class="base-header__menu-item"
                      :class="{
                        'is-active': activeKey === 'store' || isMegaMenuNavActive('store'),
                      }"
                      @pointerdown="lockCategoryMegaMenuHover('store')"
                      @click="handleStoreNavClick"
                    >
                      {{ hotProductsNavLabel }}
                    </button>
                  </div>
                  <div
                    v-for="item in topCategoryNavItems"
                    :key="item.key"
                    class="base-header__menu-item-wrapper"
                    :data-menu-key="item.key"
                    @mouseenter="handleCategoryNavMouseEnter(item.key)"
                    @mouseleave="handleCategoryNavTriggerMouseLeave(item.key, $event)"
                  >
                    <NuxtLink
                      :to="buildCategoryRoute(item.categoryId)"
                      class="base-header__menu-item"
                      :class="{
                        'is-active': activeKey === item.key || isMegaMenuNavActive(item.key),
                      }"
                      @pointerdown="lockCategoryMegaMenuHover(item.key)"
                      @click="handleCategoryNavLinkClick(item.key)"
                    >
                      {{ item.label }}
                    </NuxtLink>
                  </div>
                  <NuxtLink
                    to="/website/solutions"
                    class="base-header__menu-item"
                    :class="{ 'is-active': activeKey === 'solutions' }"
                  >
                    解决方案
                  </NuxtLink>
                  <button
                    type="button"
                    class="base-header__menu-item"
                    :class="{ 'is-active': activeKey === 'service' }"
                    @click="navigateTo('/website/service')"
                  >
                    技术支持
                  </button>
                  <NuxtLink
                    v-if="showJoinNav"
                    to="/website/join"
                    class="base-header__menu-item"
                    :class="{ 'is-active': activeKey === 'join' }"
                  >
                    工程商认证
                  </NuxtLink>
                </nav>
              </div>
              <div class="base-header__actions">
                <template v-if="isMounted">
                  <a-space :size="8" align="center">
                    <a-button
                      aria-label="Search"
                      type="text"
                      class="action-btn"
                      @click="toggleSearchPanel"
                    >
                      <template #icon><SvgIcon name="search" size="20" /></template>
                    </a-button>
                    <div
                      v-if="isLoggedIn"
                      class="cart-button-wrapper"
                      @mouseenter="handleCartButtonMouseEnter"
                      @mouseleave="handleCartButtonMouseLeave"
                    >
                      <div
                        class="action-btn action-btn--cart"
                        aria-label="Cart"
                        :class="{ 'action-btn--active': showCartPanel }"
                        @click="handleCartButtonClick"
                      >
                        <a-badge
                          :count="cartTotalCount"
                          :max-count="99"
                          :dot="false"
                          :offset="[6, 0]"
                        >
                          <SvgIcon name="add-shopping-cart" size="20" />
                        </a-badge>
                      </div>
                      <div
                        v-if="showCartPanel"
                        class="cart-panel-wrapper"
                        @mouseenter="handleCartPanelMouseEnter"
                        @mouseleave="handleCartPanelMouseLeave"
                      >
                        <div class="cart-panel-bridge" />
                        <div class="cart-panel">
                          <StoreShoppingCartPopup
                            @checkout="handleCartCheckout"
                            @go-shopping="handleCartGoShopping"
                            @view-detail="showCartPanel = false"
                          />
                        </div>
                      </div>
                    </div>
                    <div v-else class="action-btn" aria-label="Cart" @click="handleCart">
                      <a-badge
                        :count="cartTotalCount"
                        :max-count="99"
                        :dot="false"
                        :offset="[6, 0]"
                      >
                        <SvgIcon name="add-shopping-cart" size="20" />
                      </a-badge>
                    </div>
                    <a-dropdown
                      :popup-visible="
                        !isMobile && (languageDropdownVisible || isLanguageInputFocused)
                      "
                      trigger="hover"
                      position="bl"
                      class="language-dropdown"
                      :popup-max-height="false"
                      @popup-visible-change="handleLanguageDropdownVisibleChange"
                    >
                      <a-button
                        aria-label="Select region"
                        type="text"
                        class="action-btn"
                        :class="{
                          'action-btn--active':
                            !isMobile && (languageDropdownVisible || isLanguageInputFocused),
                        }"
                      >
                        <template #icon>
                          <span class="base-header__lang-icon-wrap">
                            <SvgIcon name="lang" dir="mono" size="20" />
                          </span>
                        </template>
                      </a-button>
                      <template #content>
                        <div class="language-dropdown__panel">
                          <base-language-panel
                            ref="languagePanelRef"
                            @select="handleLanguageSelect"
                            @close="handleLanguagePanelClose"
                            @focus="handleLanguageInputFocus"
                            @blur="handleLanguageInputBlur"
                            @pointerdown="handleLanguagePanelPointerDown"
                          />
                        </div>
                      </template>
                    </a-dropdown>
                    <div
                      class="user-button-wrapper"
                      @mouseenter="handleUserButtonMouseEnter"
                      @mouseleave="handleUserButtonMouseLeave"
                    >
                      <a-button
                        type="text"
                        class="action-btn"
                        :class="{ 'action-btn--active': isUserPanelVisible }"
                        aria-label="User"
                        @click.stop="handleUserButtonClick"
                      >
                        <template #icon>
                          <span class="user-action-icon">
                            <SvgIcon v-if="isUserPanelMember" name="user-login" size="20" />
                            <SvgIcon v-else name="user" size="20" />
                            <span
                              v-if="isUserPanelMember"
                              class="user-action-icon__status"
                              aria-hidden="true"
                            />
                          </span>
                        </template>
                      </a-button>
                      <div
                        v-if="isUserPanelVisible"
                        class="user-panel-wrapper"
                        :class="{
                          'user-panel-wrapper--guest': !isUserPanelMember,
                          'user-panel-wrapper--member': isUserPanelMember,
                        }"
                        @mouseenter="handleUserPanelMouseEnter"
                        @mouseleave="handleUserPanelMouseLeave"
                      >
                        <div class="user-panel-bridge" />
                        <div
                          class="user-panel"
                          :class="{
                            'user-panel--guest': !isUserPanelMember,
                            'user-panel--member': isUserPanelMember,
                          }"
                        >
                          <div v-if="!isUserPanelMember" class="user-panel__guest">
                            <div class="user-panel__guest-actions">
                              <a-button type="primary" block @click="handleLogin">登录</a-button>
                              <a-button type="default" block @click="handleRegister">注册</a-button>
                            </div>
                          </div>
                          <div v-else class="user-panel__member">
                            <div class="user-panel__member-profile">
                              <div class="user-panel__avatar">
                                <BaseImage
                                  :src="userPanelAvatarUrl"
                                  alt="用户头像"
                                  preload
                                  :preview="false"
                                  disable-oss-optimization
                                />
                              </div>
                              <div class="user-panel__member-info">
                                <div class="user-panel__member-name">
                                  {{ userPanelDisplayName }}
                                </div>
                                <div class="user-panel__member-phone">{{ userPanelPhoneText }}</div>
                              </div>
                            </div>
                            <div class="user-panel__member-menu">
                              <div
                                class="user-panel__member-menu-item"
                                @click="handleGoToAccountCenter"
                              >
                                <span>个人中心</span>
                                <IconRight
                                  aria-hidden="true"
                                  class="user-panel__member-menu-arrow"
                                />
                              </div>
                              <div
                                class="user-panel__member-menu-item"
                                @click="navigateTo('/store/account/favorites')"
                              >
                                <span>我的收藏</span>
                                <IconRight
                                  aria-hidden="true"
                                  class="user-panel__member-menu-arrow"
                                />
                              </div>
                              <div class="user-panel__member-menu-item" @click="handleLogout">
                                <span>退出登录</span>
                                <IconRight
                                  aria-hidden="true"
                                  class="user-panel__member-menu-arrow"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </a-space>
                </template>
                <div v-else class="base-header__actions-fallback" aria-hidden="true">
                  <button type="button" class="action-btn">
                    <SvgIcon name="search" size="20" />
                  </button>
                  <span class="action-btn">
                    <SvgIcon name="add-shopping-cart" size="20" />
                  </span>
                  <span class="action-btn">
                    <span class="base-header__lang-icon-wrap">
                      <SvgIcon name="lang" dir="mono" size="20" />
                    </span>
                  </span>
                  <span class="action-btn">
                    <span class="user-action-icon">
                      <SvgIcon name="user" size="20" />
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- 移动端：菜单按钮 + Logo + 操作区 -->
          <div class="base-header__navbar-mobile">
            <div class="base-header__navbar-mobile-inner" @click="handleMobileNavBarClick">
              <div class="base-header__navbar-mobile-left">
                <a-button
                  v-if="isMounted"
                  class="base-header__mobile-menu-btn"
                  type="text"
                  aria-label="Toggle menu"
                  @click.stop="toggleMenu"
                >
                  <template #icon>
                    <icon-menu v-if="!isMenuOpen" />
                    <icon-close v-else />
                  </template>
                </a-button>
                <button
                  v-else
                  type="button"
                  class="base-header__mobile-menu-btn base-header__mobile-menu-btn--fallback"
                  aria-label="Toggle menu"
                  @click.stop="toggleMenu"
                >
                  <span class="base-header__mobile-menu-icon"></span>
                </button>
              </div>
              <div class="base-header__logo">
                <NuxtLink :to="logoTo">
                  <img
                    :src="withBase('/images/brand/logo-primary.svg')"
                    alt="品牌标识"
                    class="logo-img"
                    width="84"
                    height="24"
                  />
                </NuxtLink>
              </div>
              <div class="base-header__actions">
                <template v-if="isMounted">
                  <a-space :size="2" align="center">
                    <a-button
                      aria-label="Search"
                      type="text"
                      class="action-btn base-header__action-search"
                      @click.stop="toggleSearchPanel"
                    >
                      <template #icon><SvgIcon name="search" size="20" /></template>
                    </a-button>
                    <div class="action-btn" aria-label="Cart" @click.stop="handleCart">
                      <a-badge
                        :count="cartTotalCount"
                        :max-count="99"
                        :dot="false"
                        :offset="[6, 0]"
                      >
                        <SvgIcon name="add-shopping-cart" size="20" />
                      </a-badge>
                    </div>
                    <a-button
                      v-if="isMobile"
                      aria-label="Select region"
                      type="text"
                      class="action-btn base-header__action-lang"
                      :class="{ 'action-btn--active': languageDropdownVisible }"
                      @click.stop="openMobileLanguageSheet"
                    >
                      <template #icon>
                        <span class="base-header__lang-icon-wrap">
                          <SvgIcon name="lang" dir="mono" size="20" />
                        </span>
                      </template>
                    </a-button>
                    <div
                      class="user-button-wrapper"
                      @mouseenter="handleUserButtonMouseEnter"
                      @mouseleave="handleUserButtonMouseLeave"
                    >
                      <a-button
                        type="text"
                        class="action-btn"
                        :class="{ 'action-btn--active': isUserPanelVisible }"
                        aria-label="User"
                        @click.stop="handleUserButtonClick"
                      >
                        <template #icon>
                          <span class="user-action-icon">
                            <SvgIcon v-if="isUserPanelMember" name="user-login" size="20" />
                            <SvgIcon v-else name="user" size="20" />
                            <span
                              v-if="isUserPanelMember"
                              class="user-action-icon__status"
                              aria-hidden="true"
                            />
                          </span>
                        </template>
                      </a-button>
                      <div
                        v-if="isUserPanelVisible"
                        class="user-panel-wrapper"
                        :class="{
                          'user-panel-wrapper--guest': !isUserPanelMember,
                          'user-panel-wrapper--member': isUserPanelMember,
                        }"
                        @mouseenter="handleUserPanelMouseEnter"
                        @mouseleave="handleUserPanelMouseLeave"
                      >
                        <div class="user-panel-bridge" />
                        <div
                          class="user-panel"
                          :class="{
                            'user-panel--guest': !isUserPanelMember,
                            'user-panel--member': isUserPanelMember,
                          }"
                        >
                          <div v-if="!isUserPanelMember" class="user-panel__guest">
                            <div class="user-panel__guest-actions">
                              <a-button type="primary" block @click="handleLogin">登录</a-button>
                              <a-button type="default" block @click="handleRegister">注册</a-button>
                            </div>
                          </div>
                          <div v-else class="user-panel__member">
                            <div class="user-panel__member-profile">
                              <div class="user-panel__avatar">
                                <BaseImage
                                  :src="userPanelAvatarUrl"
                                  alt="用户头像"
                                  preload
                                  :preview="false"
                                  disable-oss-optimization
                                />
                              </div>
                              <div class="user-panel__member-info">
                                <div class="user-panel__member-name">
                                  {{ userPanelDisplayName }}
                                </div>
                                <div class="user-panel__member-phone">{{ userPanelPhoneText }}</div>
                              </div>
                            </div>
                            <div class="user-panel__member-menu">
                              <div
                                class="user-panel__member-menu-item"
                                @click="handleGoToAccountCenter"
                              >
                                <span>个人中心</span>
                                <IconRight
                                  aria-hidden="true"
                                  class="user-panel__member-menu-arrow"
                                />
                              </div>
                              <div
                                class="user-panel__member-menu-item"
                                @click="navigateTo('/store/account/favorites')"
                              >
                                <span>我的收藏</span>
                                <IconRight
                                  aria-hidden="true"
                                  class="user-panel__member-menu-arrow"
                                />
                              </div>
                              <div class="user-panel__member-menu-item" @click="handleLogout">
                                <span>退出登录</span>
                                <IconRight
                                  aria-hidden="true"
                                  class="user-panel__member-menu-arrow"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </a-space>
                </template>
                <div
                  v-else
                  class="base-header__actions-fallback base-header__actions-fallback--mobile"
                  aria-hidden="true"
                >
                  <button type="button" class="action-btn">
                    <SvgIcon name="search" size="20" />
                  </button>
                  <span class="action-btn">
                    <SvgIcon name="add-shopping-cart" size="20" />
                  </span>
                  <span class="action-btn">
                    <span class="base-header__lang-icon-wrap">
                      <SvgIcon name="lang" dir="mono" size="20" />
                    </span>
                  </span>
                  <span class="action-btn">
                    <span class="user-action-icon">
                      <SvgIcon name="user" size="20" />
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- 自定义内容插槽（当 type === 'custom' 时显示） -->
        <div v-if="type === 'custom'" class="base-header__custom" :style="customHeaderSurfaceStyle">
          <div class="base-container">
            <slot name="custom-content" />
          </div>
        </div>
      </div>

      <transition
        name="category-mega-menu-drop"
        :duration="{ enter: 400, leave: 400 }"
        @after-leave="handleCategoryMegaMenuAfterLeave"
      >
        <div
          v-show="showCategoryMegaMenu && currentMegaMenu"
          class="category-mega-menu-layer"
          @mouseenter="handleCategoryMegaMenuMouseEnter"
          @mouseleave="handleCategoryMegaMenuMouseLeave"
        >
          <div
            v-for="menu in cachedCategoryMegaMenus"
            v-show="activeCategoryMegaMenuKey === menu.key"
            :key="menu.key"
            class="category-mega-menu"
            :data-menu-key="menu.key"
          >
            <div class="base-container category-mega-menu__inner">
              <aside class="category-mega-menu__sidebar">
                <button
                  v-for="anchor in menu.anchors"
                  :key="anchor.key"
                  :ref="(el) => setMegaMenuAnchorRef(menu.key, anchor.key, el)"
                  type="button"
                  class="category-mega-menu__anchor"
                  :class="{
                    'is-active':
                      (hoveredMegaMenuAnchorKey || activeMegaMenuAnchorKey) === anchor.key,
                  }"
                  @mouseenter="handleMegaMenuAnchorHover(menu.key, anchor.key)"
                  @mouseleave="handleMegaMenuAnchorLeave"
                  @focus="handleMegaMenuAnchorHover(menu.key, anchor.key)"
                  @click="handleMegaMenuAnchorClick(menu.key, anchor.key)"
                >
                  <span class="category-mega-menu__anchor-text">{{ anchor.label }}</span>
                </button>
              </aside>

              <div class="category-mega-menu__main">
                <button
                  type="button"
                  class="category-mega-menu__close"
                  aria-label="关闭分类弹窗"
                  @click="handleCategoryMegaMenuCloseClick"
                >
                  <icon-close />
                </button>

                <div
                  v-if="isCategoryMegaMenuLoading(menu.key)"
                  class="category-mega-menu__skeleton"
                >
                  <div v-for="i in 6" :key="i" class="category-mega-menu__skeleton-card"></div>
                </div>

                <div
                  v-else
                  :ref="(el) => setMegaMenuContentRef(menu.key, el)"
                  class="category-mega-menu__content"
                  @scroll="handleMegaMenuContentScroll(menu.key)"
                  @wheel="handleMegaMenuContentWheel(menu.key, $event)"
                >
                  <section
                    v-for="section in menu.sections"
                    :key="section.key"
                    :ref="(el) => setMegaMenuSectionRef(menu.key, section.key, el)"
                    class="category-mega-menu__section"
                  >
                    <div class="category-mega-menu__section-header">
                      <h3 class="category-mega-menu__section-title">{{ section.title }}</h3>
                      <button
                        v-if="section.allRoute"
                        type="button"
                        class="category-mega-menu__view-all"
                        :aria-label="`查看${section.title}全部商品`"
                        @click="handleMegaMenuViewAll(section)"
                      >
                        <span>查看全部</span>
                        <IconRight class="category-mega-menu__view-all-icon" aria-hidden="true" />
                      </button>
                    </div>
                    <div class="category-mega-menu__cards">
                      <template
                        v-for="entry in getMegaMenuSectionEntries(menu.key, section)"
                        :key="entry.key"
                      >
                        <div
                          v-if="entry.type === 'product'"
                          class="category-mega-menu__card"
                          :data-prod-id="entry.product?.prodId || entry.product?.id"
                          @click.capture="handleMegaMenuProductClick(menu.key, entry.product)"
                        >
                          <StoreProductListCard
                            :product="entry.product"
                            :show-compare="false"
                          />
                        </div>
                        <button
                          v-else
                          type="button"
                          class="category-mega-menu__all-card"
                          @click="handleMegaMenuViewAll(section)"
                        >
                          <span class="category-mega-menu__all-card-content">
                            <span class="category-mega-menu__all-card-text">查看全部</span>
                            <span class="category-mega-menu__all-card-icon-wrap">
                              <IconRight class="category-mega-menu__all-card-icon" />
                            </span>
                          </span>
                        </button>
                      </template>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>
          <div
            class="category-mega-menu-layer__dismiss-zone"
            aria-hidden="true"
            @pointerenter="handleCategoryMegaMenuDismissZoneEnter"
          ></div>
        </div>
      </transition>

      <!-- 移动端菜单遮罩 -->
      <transition name="fade">
        <div v-if="isMenuOpen" class="mobile-overlay" @click="closeMenu" />
      </transition>

      <!-- 移动端菜单 -->
      <transition @before-enter="onBeforeEnter" @enter="onEnter" @leave="onLeave">
        <div v-if="isMenuOpen" class="mobile-menu">
          <div class="mobile-menu__content">
            <template v-if="isHeaderNavLoading">
              <div
                v-for="item in headerNavSkeletonItems"
                :key="item.key"
                class="mobile-menu__link mobile-menu__link--skeleton"
                aria-hidden="true"
              >
                <a-skeleton animation>
                  <a-skeleton-line :rows="1" :widths="[item.mobileWidth]" />
                </a-skeleton>
              </div>
            </template>
            <template v-else>
              <NuxtLink
                v-if="showHotProductsNav"
                to="/store"
                class="mobile-menu__link"
                :class="{
                  'mobile-menu__link--active': activeKey === 'store' && route.path === '/store',
                }"
                @click="closeMenu"
              >
                {{ hotProductsNavLabel }}
              </NuxtLink>
              <NuxtLink
                v-for="item in mobileMenuCategoryItems"
                :key="item.key"
                v-slot="{ href, navigate }"
                :to="item.route"
                custom
              >
                <div
                  class="mobile-menu__group"
                  :class="{ 'is-expanded': isMobileMenuItemExpanded(item.key) }"
                >
                  <div v-if="item.children.length" class="mobile-menu__row">
                    <a
                      :href="href"
                      class="mobile-menu__row-link"
                      :class="{ 'mobile-menu__row-link--active': item.active }"
                      @click="handleMobileMenuNavigate($event, navigate)"
                    >
                      {{ item.label }}
                    </a>
                    <button
                      type="button"
                      class="mobile-menu__toggle"
                      :aria-label="isMobileMenuItemExpanded(item.key) ? '收起分类' : '展开分类'"
                      :aria-expanded="isMobileMenuItemExpanded(item.key)"
                      @click="toggleMobileMenuItem(item.key)"
                    >
                      <IconMinus
                        v-if="isMobileMenuItemExpanded(item.key)"
                        class="mobile-menu__toggle-icon"
                        aria-hidden="true"
                      />
                      <IconPlus v-else class="mobile-menu__toggle-icon" aria-hidden="true" />
                    </button>
                  </div>
                  <NuxtLink
                    v-else
                    :to="item.route"
                    class="mobile-menu__link"
                    :class="{ 'mobile-menu__link--active': item.active }"
                    @click="closeMenu"
                  >
                    {{ item.label }}
                  </NuxtLink>
                  <div
                    v-if="item.children.length && isMobileMenuItemExpanded(item.key)"
                    class="mobile-menu__sublist"
                  >
                    <NuxtLink
                      v-for="child in item.children"
                      :key="child.key"
                      :to="child.route"
                      class="mobile-menu__subitem"
                      @click="closeMenu"
                    >
                      {{ child.label }}
                    </NuxtLink>
                  </div>
                </div>
              </NuxtLink>
              <NuxtLink
                to="/website/solutions"
                class="mobile-menu__link"
                :class="{ 'mobile-menu__link--active': activeKey === 'solutions' }"
                @click="closeMenu"
              >
                解决方案
              </NuxtLink>
              <NuxtLink
                to="/website/service"
                class="mobile-menu__link"
                :class="{ 'mobile-menu__link--active': activeKey === 'service' }"
                @click="closeMenu"
              >
                技术支持
              </NuxtLink>
              <NuxtLink
                v-if="showJoinNav"
                to="/website/join"
                class="mobile-menu__link"
                :class="{ 'mobile-menu__link--active': activeKey === 'join' }"
                @click="closeMenu"
              >
                工程商认证
              </NuxtLink>
            </template>
            <!-- 云管平台入口暂时隐藏。
            <span
              v-if="isAuthenticated"
              class="mobile-menu__link"
              @click="handleCloudPlatformMobile"
              >云管平台</span
            >
            -->
          </div>
        </div>
      </transition>
    </template>

    <!-- 移动端语言选择抽屉 -->
    <base-action-sheet
      v-if="isMounted && isMobile"
      v-model:visible="languageDropdownVisible"
      title="选择语言"
      :show-cancel="false"
      max-height="80vh"
    >
      <template #default>
        <base-language-panel :show-close="false" @select="handleLanguageSelect" />
      </template>
    </base-action-sheet>

    <!-- 搜索弹窗：移动端业务容器内部复用纯展示 MobileSearchPanel -->
    <StoreMobileSearchPanel v-if="isMobile" v-model:visible="showSearchPanel" />
    <base-search-panel v-else v-model:visible="showSearchPanel" @search="handleSearch" />
  </header>
</template>

<script setup>
import { IconMenu, IconClose, IconRight, IconPlus, IconMinus } from '@arco-design/web-vue/es/icon'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useCategoryStore } from '~/store/common/category'
import { useUserStore } from '~/store/common/user'
import { useStoreAggregatePageStore } from '~/store/store/aggregatePage'
import { useStoreCartStore } from '~/store/store/cart'
import { authApi } from '~/api/auth'
import { useAuth } from '~/composables/useAuth'
import { useAuthLinks } from '~/composables/useAuthLinks'
import { useMediaQuery } from '~/composables/useMediaQuery'
import { useStoreAccountProfileDisplay } from '~/composables/store/useStoreAccountProfileDisplay'
import BaseImage from '~/components/base/Image.vue'
import StoreMobileSearchPanel from '~/components/store/search/MobilePanel.vue'
// import { resolveImsBaseUrl } from '~/utils/imsRuntime'
import { BaseMessage } from '~/utils/baseMessage'
import { lockBodyScroll, unlockBodyScroll } from '~/utils/modal'
import CommonModal from '~/utils/commonModal'
import { productApi } from '~/api/store/product'
import { aggregatePageApi } from '~/api/common/aggregate-page'
import { PRODUCT_TAG_CODE } from '~/utils/product'
import { hasJoinNavigationIdentity, shouldShowJoinNavigation } from '~/utils/store/userIdentity'
import { mapAggregateTopCategoryNavSections } from '~/utils/store/topCategoryNav'
import { normalizeAggregatePagePayload } from '~/utils/store/aggregatePage'
import {
  buildMegaMenuProductPreview,
  buildPrimaryCategoryMegaMenu,
} from '~/utils/store/categoryMegaMenu'
import {
  buildHotProductsMegaMenuQuery,
  buildMegaMenuFromAggregatePage,
  HOT_PRODUCTS_MEGA_MENU_ANCHOR_CATEGORY_ID,
  MEGA_MENU_VISIBLE_CARD_COUNT,
} from '~/utils/store/aggregateMegaMenu'
import {
  MEGA_MENU_SCROLL_CLOSE_DELAY,
  MEGA_MENU_SCROLL_IDLE_CONFIRM_DELAY,
  resolveMegaMenuScrollBoundary,
} from '~/utils/store/megaMenuScrollBoundary'
import { resolveMegaMenuLoading } from '~/utils/store/megaMenuLoading'
import { createPublicContentSsrRequestConfig } from '~/utils/website/ssrRequestPolicy'
import { trackClickEvent } from '~/composables/usePageTracking'
import {
  buildMegaMenuHoverTrack,
  buildMegaMenuProductCardEvents,
  buildProductClickPayload,
  resolveMegaMenuPrefix,
} from '~/utils/tracking/eventBuilders'
import { buildProductTrackFields } from '~/utils/tracking/helpers'
import {
  buildScrollDepthProperties,
  calcScrollPercent,
  getNewlyReachedScrollThresholds,
} from '~/utils/tracking/scrollDepth'
import {
  hasExposedInPage,
  markExposedInPage,
  safeTrack,
  useViewportExposure,
} from '~/utils/tracker'

const props = defineProps({
  /**
   * @description Header variant
   * @type {string}
   * @default 'default'
   * @values 'default' | 'simple' | 'custom'
   */
  type: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'simple', 'custom'].includes(value),
  },
  logoTo: {
    type: String,
    default: '/',
  },
  showNav: {
    type: Boolean,
    default: true,
  },
})

const { withBase, openExternal } = useBasePath()
const route = useRoute()
const logoTo = computed(() => props.logoTo)
const isMenuOpen = ref(false)
const showUserPanel = ref(false) // User panel visibility
let userPanelTimeout = null // Timer used to delay user panel hiding
const USER_PANEL_HIDE_DELAY = 200 // Short hide delay keeps hover interactions responsive
const LANGUAGE_PANEL_BLUR_DELAY = 160
let languageInputBlurTimer = null

// Keep breakpoint logic aligned with useMediaQuery and styles/_variables.scss.
const { isBelowDesktop } = useMediaQuery()
const isMobile = isBelowDesktop

const isHeaderClientReady = () => import.meta.client && window.__TENDA_HEADER_CLIENT_READY === true

// Mounted flag used to avoid SSR hydration mismatches.
// After the first client mount, route-created Header instances can render actions immediately.
const isMounted = ref(isHeaderClientReady())

// Language dropdown visibility state.
const languageDropdownVisible = ref(false)
const isLanguageInputFocused = ref(false)

const languagePanelRef = ref(null)

const clearLanguageInputBlurTimer = () => {
  if (languageInputBlurTimer) {
    clearTimeout(languageInputBlurTimer)
    languageInputBlurTimer = null
  }
}

const handleLanguagePanelClose = () => {
  clearLanguageInputBlurTimer()
  languageDropdownVisible.value = false
  isLanguageInputFocused.value = false
}

const handleLanguageSelect = () => {
  handleLanguagePanelClose()
}

const handleLanguageDropdownVisibleChange = (visible) => {
  languageDropdownVisible.value = visible
  if (visible) {
    clearLanguageInputBlurTimer()
  }
}

const handleLanguageInputFocus = () => {
  clearLanguageInputBlurTimer()
  isLanguageInputFocused.value = true
}

const handleLanguageInputBlur = () => {
  clearLanguageInputBlurTimer()
  languageInputBlurTimer = setTimeout(() => {
    isLanguageInputFocused.value = false
    languageInputBlurTimer = null
  }, LANGUAGE_PANEL_BLUR_DELAY)
}

const handleLanguagePanelPointerDown = () => {
  clearLanguageInputBlurTimer()
}

// Store instances
const categoryStore = useCategoryStore()
const aggregatePageStore = useStoreAggregatePageStore()
const userStore = useUserStore()
const cartStore = useStoreCartStore()
const { logout } = useAuth()
const publicContentSsrEnabled = usePublicContentSsrEnabled()

const { data: initialPublicNavigation } = await useAsyncData(
  'base-header-public-navigation',
  async () => {
    if (import.meta.client) return { categories: [], menuPages: [] }

    const requestConfig = createPublicContentSsrRequestConfig({ timeout: 3_000 })
    const categoryRequest = userStore.isLoggedIn
      ? Promise.resolve([])
      : categoryStore.fetchCategoryInfo({ authKey: 'guest', requestConfig }).catch((error) => {
          console.error('[Header] Failed to fetch SSR category tree:', error)
          return []
        })
    const [categories, menuPages] = await Promise.all([
      categoryRequest,
      aggregatePageStore.fetchMenuPages({ requestConfig }),
    ])
    return { categories, menuPages }
  },
  usePublicContentAsyncDataOptions({ default: () => ({ categories: [], menuPages: [] }) })
)

if (publicContentSsrEnabled.value && (import.meta.server || useNuxtApp().isHydrating)) {
  const initialMenuPages = Array.isArray(initialPublicNavigation.value.menuPages)
    ? initialPublicNavigation.value.menuPages
    : []

  categoryStore.$patch({
    categoryTree: Array.isArray(initialPublicNavigation.value.categories)
      ? initialPublicNavigation.value.categories
      : [],
    fetched: !userStore.isLoggedIn,
    fetchedAuthKey: userStore.isLoggedIn ? '' : 'guest',
    fetchedClient: false,
    loading: false,
  })
  aggregatePageStore.$patch({
    menuPages: initialMenuPages,
    // SSR-off and failed/empty SSR snapshots must keep the original client
    // request lifecycle active instead of caching an unverified empty menu.
    menuPagesLoaded: initialMenuPages.length > 0,
    menuPagesLoading: false,
    menuPagesFailed: false,
  })
}

const { navigateToLogin, navigateToRegister } = useAuthLinks()

const showCartPanel = ref(false)
let cartPanelTimeout = null

const showCategoryMegaMenu = ref(false)
const activeCategoryMegaMenuKey = ref('')
const megaMenuViewportExposure = useViewportExposure({ threshold: 0.5 })
const megaMenuScrollDepthReached = ref({})
/** @type {import('vue').Ref<{ menuKey: string, openedAt: number }|null>} */
const megaMenuHoverTiming = ref(null)

/**
 * @description 上报 mega menu 悬停页停留时长
 * @param {string} [menuKey]
 * @returns {void}
 */
const flushMegaMenuHoverDuration = (menuKey) => {
  const timing = megaMenuHoverTiming.value
  if (!timing?.openedAt) return
  const targetKey = menuKey || timing.menuKey
  if (!targetKey || timing.menuKey !== targetKey) return

  const durationMs = Date.now() - timing.openedAt
  megaMenuHoverTiming.value = null
  if (durationMs <= 0) return

  const prefix = resolveMegaMenuPrefix(targetKey, topCategoryNavItems.value)
  const config = buildMegaMenuHoverTrack(targetKey, topCategoryNavItems.value)
  safeTrack({
    eventName: `pv_${prefix}_Hover_Duration`,
    eventType: 'timing',
    targetType: config.targetType,
    targetId: String(config.targetId),
    targetName: config.targetName || config.pageName || '',
    pageName: config.pageName,
    properties: {
      ...(config.properties || {}),
      total_duration_ms: durationMs,
      avg_per_session: true,
    },
  })
}

/**
 * @description 开始 mega menu 悬停停留计时（切换菜单时先结算上一段）
 * @param {string} menuKey
 * @returns {void}
 */
const beginMegaMenuHoverTiming = (menuKey) => {
  if (!menuKey) return
  const current = megaMenuHoverTiming.value
  if (current?.menuKey && current.menuKey !== menuKey) {
    flushMegaMenuHoverDuration(current.menuKey)
  }
  if (megaMenuHoverTiming.value?.menuKey === menuKey) return
  megaMenuHoverTiming.value = { menuKey, openedAt: Date.now() }
}

/**
 * @description 上报 mega menu 悬停页滚动深度（按档升序，短页仅报 100）
 * @param {string} menuKey
 * @param {number} depthPercent
 * @returns {void}
 */
const trackMegaMenuScrollDepth = (menuKey, depthPercent) => {
  const prefix = resolveMegaMenuPrefix(menuKey, topCategoryNavItems.value)
  const depthValue = Number(depthPercent)
  const depthKey = `mega_menu_scroll_${menuKey}_${depthValue}`
  if (megaMenuScrollDepthReached.value[depthKey] || hasExposedInPage(depthKey)) return
  megaMenuScrollDepthReached.value = { ...megaMenuScrollDepthReached.value, [depthKey]: true }
  markExposedInPage(depthKey)

  const config = buildMegaMenuHoverTrack(menuKey, topCategoryNavItems.value)
  const pageName = config.pageName || `${prefix}悬浮页`
  safeTrack({
    eventName: `pv_${prefix}_Hover_Scroll_Depth`,
    eventType: 'impression',
    targetType: config.targetType || `${prefix.toLowerCase()}_hover`,
    targetId: String(config.targetId || menuKey),
    targetName: config.targetName || config.properties?.category_name || pageName,
    pageName,
    scroll_depth: depthValue,
    properties: buildScrollDepthProperties(depthValue, {
      ...(config.properties || {}),
      avg_per_session: true,
    }),
  })
}

/**
 * @description 按百分比升序补报 mega menu 滚动档位
 * @param {string} menuKey
 * @param {number} percent
 * @param {{ shortPage?: boolean }} [options]
 * @returns {void}
 */
const trackMegaMenuScrollDepthByPercent = (menuKey, percent, options = {}) => {
  const reached = new Set(
    Object.keys(megaMenuScrollDepthReached.value)
      .filter((key) => key.startsWith(`mega_menu_scroll_${menuKey}_`))
      .map((key) => Number(key.split('_').pop()))
      .filter((n) => Number.isFinite(n))
  )
  getNewlyReachedScrollThresholds(percent, reached, options).forEach((threshold) => {
    trackMegaMenuScrollDepth(menuKey, threshold)
  })
}

/**
 * @description 上报 mega menu 悬停页曝光
 * @param {string} menuKey
 * @returns {void}
 */
const trackMegaMenuHoverExposure = (menuKey) => {
  const config = buildMegaMenuHoverTrack(menuKey, topCategoryNavItems.value)
  if (hasExposedInPage(config.pageKey)) return
  markExposedInPage(config.pageKey)
  safeTrack({
    eventName: config.pvEvent,
    eventType: 'impression',
    targetType: config.targetType,
    targetId: String(config.targetId),
    targetName: config.targetName || config.properties?.category_name || config.pageName || '',
    pageName: config.pageName,
    properties: { ...(config.properties || {}) },
  })
}

/**
 * @description 从 mega menu 缓存解析完整商品对象
 * @param {string} menuKey
 * @param {string|number} prodId
 * @returns {Object}
 */
const resolveMegaMenuProduct = (menuKey, prodId) => {
  const cache = megaMenuProductCache.value[menuKey]
  if (!cache) return { prodId, id: prodId }
  for (const products of Object.values(cache)) {
    if (!Array.isArray(products)) continue
    const found = products.find(
      (item) => String(item?.prodId ?? item?.id ?? '') === String(prodId)
    )
    if (found) return found
  }
  return { prodId, id: prodId }
}

/**
 * @description 初始化 mega menu 商品卡片曝光监听
 * @param {string} menuKey
 * @returns {void}
 */
const initMegaMenuProductExposure = (menuKey) => {
  if (!import.meta.client || !menuKey) return
  nextTick(() => {
    megaMenuViewportExposure.observe(
      `.category-mega-menu[data-menu-key="${menuKey}"] .category-mega-menu__card[data-prod-id]`,
      (el) => {
        const prodId = el?.dataset?.prodId
        if (!prodId) return
        const exposureKey = `mega_menu_${menuKey}_${prodId}`
        if (hasExposedInPage(exposureKey)) return
        markExposedInPage(exposureKey)
        const prefix = resolveMegaMenuPrefix(menuKey, topCategoryNavItems.value)
        const product = resolveMegaMenuProduct(menuKey, prodId)
        const fields = buildProductTrackFields(product)
        const cardEvents = buildMegaMenuProductCardEvents(prefix)
        const navItem = topCategoryNavItems.value.find((item) => item.key === menuKey)
        safeTrack({
          eventName: cardEvents.exposure,
          eventType: 'impression',
          targetType: 'mega_menu_product',
          targetId: fields.targetId,
          targetName: fields.targetName,
          pageName: `${prefix}悬浮页`,
          product,
          item: product,
          sourceType: 'mega_menu',
          sourceId: String(navItem?.categoryId || menuKey || ''),
          sourceName: navItem?.label || `${prefix}悬浮页`,
          properties: {
            ...fields.properties,
            source_type: 'mega_menu',
            source_id: String(navItem?.categoryId || menuKey || ''),
            source_name: navItem?.label || `${prefix}悬浮页`,
            from_page: 'mega_menu',
          },
        })
      },
      `mega-menu-products-${menuKey}`
    )
    megaMenuViewportExposure.rescan(`mega-menu-products-${menuKey}`)
  })
}

/**
 * @description 上报 mega menu 商品卡片点击
 * @param {string} menuKey
 * @param {Object} product
 * @returns {void}
 */
const handleMegaMenuProductClick = (menuKey, product) => {
  if (!product) return
  const prefix = resolveMegaMenuPrefix(menuKey, topCategoryNavItems.value)
  const cardEvents = buildMegaMenuProductCardEvents(prefix)
  const navItem = topCategoryNavItems.value.find((item) => item.key === menuKey)
  trackClickEvent(
    buildProductClickPayload({
      pvEvent: cardEvents.click,
      pageName: `${prefix}悬浮页`,
      product,
      targetType: 'mega_menu_product',
      trackContext: {
        sourceType: 'mega_menu',
        sourceId: String(navItem?.categoryId || menuKey || ''),
        sourceName: navItem?.label || `${prefix}悬浮页`,
        fromPage: 'mega_menu',
        pageName: `${prefix}悬浮页`,
      },
    })
  )
}
const activeMegaMenuAnchorKey = ref('')
const hoveredMegaMenuAnchorKey = ref('')
const megaMenuProductsLoading = ref({})
const megaMenuProductCache = ref({})
const cachedCategoryMegaMenuKeys = ref([])
const megaMenuContentRefs = ref({})
const megaMenuAnchorRefs = ref({})
const megaMenuSectionRefs = ref({})
const megaMenuBottomScrollArmed = ref({})
const megaMenuBottomScrollArmTimers = new Map()
const megaMenuBottomScrollCloseTimers = new Map()
const megaMenuProgrammaticScrollKeys = new Set()
const megaMenuProgrammaticScrollTimers = new Map()
let categoryMegaMenuShowTimer = null
let pendingCategoryMegaMenuKey = ''
let categoryMegaMenuHideTimer = null
let categoryMegaMenuRequestToken = 0
/** @description 按菜单项记录点击锁定，跨路由保持，仅阻止对应项 hover 重开 */
const categoryMegaMenuHoverLockedKeys = useState('categoryMegaMenuHoverLockedKeys', () => ({}))
const aggregateMegaMenuPages = ref({})
const aggregateMegaMenuLoading = ref({})
let hasHeaderOverlayScrollLock = false
let gsapInstance = null
let gsapLoadPromise = null
let isHeaderUnmounted = false

const loadGsap = async () => {
  if (!import.meta.client) return null
  if (gsapInstance) return gsapInstance
  gsapLoadPromise ??= import('gsap').then(({ gsap }) => {
    gsapInstance = gsap
    return gsap
  })
  return gsapLoadPromise
}

const shouldLockHeaderOverlayScroll = computed(() => {
  return (
    isMenuOpen.value ||
    (!isMobile.value &&
      (showCategoryMegaMenu.value || languageDropdownVisible.value || isLanguageInputFocused.value))
  )
})
const headerNavSkeletonItems = [
  { key: 'nav-skeleton-1', desktopWidth: 72, mobileWidth: 120 },
  { key: 'nav-skeleton-2', desktopWidth: 88, mobileWidth: 136 },
  { key: 'nav-skeleton-3', desktopWidth: 76, mobileWidth: 108 },
]
const mobileExpandedMenuKeys = ref([])

const cartTotalCount = computed(() => cartStore.headerTotalCount)

const AUTH_FAILURE_STATUS = new Set([401, 403])
const AUTH_FAILURE_CODES = new Set(['A00004', 'A00005', 'A00007'])

const isAuthFailureError = (error) => {
  const status = Number(error?.response?.status)
  if (AUTH_FAILURE_STATUS.has(status)) return true

  const code = String(error?.code || error?.raw?.code || error?.response?.data?.code || '')
  if (AUTH_FAILURE_CODES.has(code)) return true

  const message = String(
    error?.message || error?.response?.data?.message || error?.response?.data?.msg || ''
  ).toLowerCase()

  return message.includes('unauthorized') || message.includes('登录已失效')
}

const fetchImsUserInfoSilently = async () => {
  if (!userStore.accessToken) return

  try {
    await userStore.fetchImsUserInfo()
  } catch (error) {
    console.error('[Header] Failed to fetch IMS user info:', error)
  }
}

// Fetch user info
const getUserInfo = async () => {
  try {
    await userStore.fetchUserInfo()
    try {
      await userStore.fetchImsUserInfo()
    } catch (error) {
      console.warn('[Header] IMS user info unavailable after mall user info loaded.', error)
    }
  } catch (error) {
    if (isAuthFailureError(error)) {
      await userStore.logout(true)
      return
    }

    console.warn('[Header] Mall user info unavailable; keeping IMS login state.', error)
    await fetchImsUserInfoSilently()
  }
}

onMounted(() => {
  if (!categoryStore.fetchedClient && !categoryStore.loading) {
    categoryStore.fetchCategoryInfo().catch((error) => {
      console.error('[Header] Failed to fetch category tree:', error)
    })
  }
  aggregatePageStore.fetchMenuPages()
})

async function fetchAggregateMegaMenuPage(menuKey, loader) {
  if (Object.prototype.hasOwnProperty.call(aggregateMegaMenuPages.value, menuKey)) {
    return
  }

  aggregateMegaMenuLoading.value = {
    ...aggregateMegaMenuLoading.value,
    [menuKey]: true,
  }

  try {
    const response = await loader()
    aggregateMegaMenuPages.value = {
      ...aggregateMegaMenuPages.value,
      [menuKey]: normalizeAggregatePagePayload(response),
    }
  } catch (error) {
    aggregateMegaMenuPages.value = {
      ...aggregateMegaMenuPages.value,
      [menuKey]: null,
    }
    console.error(`[Header] Failed to fetch aggregate mega menu (${menuKey}):`, error)
  } finally {
    aggregateMegaMenuLoading.value = {
      ...aggregateMegaMenuLoading.value,
      [menuKey]: false,
    }
  }
}

async function fetchHotProductsAggregatePage() {
  return fetchAggregateMegaMenuPage('store', () =>
    aggregatePageApi.getCategoryPage(
      HOT_PRODUCTS_MEGA_MENU_ANCHOR_CATEGORY_ID,
      buildHotProductsMegaMenuQuery()
    )
  )
}

const aggregatePageMenuFailed = computed(() => aggregatePageStore.menuPagesFailed)
const shouldUseAggregatePageMenu = computed(() => aggregatePageStore.shouldUseMenuPages)
const isAggregatePageMenuPending = computed(() => aggregatePageStore.isMenuPagesPending)

const isHeaderNavLoading = computed(() => {
  return props.showNav && isAggregatePageMenuPending.value
})

const hotProductsAggregatePage = computed(() => aggregatePageStore.hotProductsPage)

const showHotProductsNav = computed(() => {
  if (isAggregatePageMenuPending.value) return false
  if (!shouldUseAggregatePageMenu.value) return true
  return Boolean(hotProductsAggregatePage.value)
})

const hotProductsNavLabel = computed(() => {
  return hotProductsAggregatePage.value?.pageName || '爆款精品'
})

const aggregateTopCategoryNavItems = computed(() => {
  return mapAggregateTopCategoryNavSections(
    categoryStore.categoryList,
    aggregatePageStore.categoryPages
  )
})

const topCategoryNavItems = computed(() => {
  if (isAggregatePageMenuPending.value) {
    return []
  }
  if (aggregatePageMenuFailed.value) {
    return []
  }
  return aggregateTopCategoryNavItems.value
})

/**
 * 参考 vmall 主导航：首次悬停保留 80ms 意图确认时间，
 * 避免鼠标从浏览器顶部快速划向正文时误开整屏面板。
 * 面板已经打开后的分类横向切换不使用该延迟。
 */
const CATEGORY_MEGA_MENU_SHOW_DELAY = 80
const CATEGORY_MEGA_MENU_HIDE_DELAY = 180
const CATEGORY_MEGA_MENU_SCROLL_OFFSET = 56
const CATEGORY_MEGA_MENU_PROGRAMMATIC_SCROLL_GUARD_DELAY = 150
const CATEGORY_MEGA_MENU_VISIBLE_CARD_COUNT = MEGA_MENU_VISIBLE_CARD_COUNT
const HOT_PRODUCTS_FALLBACK_LABEL_PICK = [PRODUCT_TAG_CODE.NEW, PRODUCT_TAG_CODE.HOT]

/**
 * @description Build the store category page route.
 * @param {string|number} primaryCategoryId - Primary category id.
 * @param {string|number} [secondaryCategoryId] - Optional secondary category id.
 * @returns {string|Object|null} Category page route.
 */
const buildCategoryRoute = (primaryCategoryId, secondaryCategoryId = '') => {
  if (!primaryCategoryId) return null
  const path = `/store/category/${encodeURIComponent(primaryCategoryId)}`
  if (!secondaryCategoryId) return path
  return {
    path,
    query: { secondaryCategoryId: String(secondaryCategoryId) },
  }
}

const mobileMenuCategoryItems = computed(() => {
  return topCategoryNavItems.value.map((item) => {
    const children = (Array.isArray(item.categories) ? item.categories : [])
      .filter((child) => child?.categoryId && child?.categoryName)
      .map((child) => ({
        key: `${item.key}-${child.categoryId}`,
        label: child.categoryName,
        route: buildCategoryRoute(item.categoryId, child.categoryId),
      }))

    return {
      key: item.key,
      label: item.label,
      route: buildCategoryRoute(item.categoryId),
      active: activeKey.value === item.key,
      children,
    }
  })
})

const isMobileMenuItemExpanded = (key) => mobileExpandedMenuKeys.value.includes(key)

const toggleMobileMenuItem = (key) => {
  if (!key) return
  if (isMobileMenuItemExpanded(key)) {
    mobileExpandedMenuKeys.value = mobileExpandedMenuKeys.value.filter((itemKey) => itemKey !== key)
    return
  }
  mobileExpandedMenuKeys.value = [key]
}

const handleMobileMenuNavigate = (event, navigate) => {
  closeMenu()
  navigate(event)
}

/**
 * @description Build product list route for mega menu "view all" entries.
 * @param {string|number} primaryCategoryId - Primary category id.
 * @param {string|number} [secondaryCategoryId] - Optional secondary category id.
 * @returns {Object|null} Product list route.
 */
const buildMegaMenuProductListRoute = (primaryCategoryId, secondaryCategoryId = '') => {
  if (!primaryCategoryId) return null
  return {
    path: '/store/products',
    query: {
      categoryId: String(primaryCategoryId),
      secondaryCategoryId: secondaryCategoryId ? String(secondaryCategoryId) : undefined,
    },
  }
}

const buildMegaMenuSections = (
  nodes = [],
  keyPrefix = '',
  routeBuilder = () => null,
  primaryCategoryId = ''
) => {
  return nodes
    .map((node, index) => {
      const categoryId = node?.categoryId

      return {
        key: `${keyPrefix}${categoryId || index}`,
        title: node.categoryName || '',
        categoryId,
        primaryCategoryId,
        productIds: [],
        hasMore: false,
        loadFromSearch: true,
        allRoute: routeBuilder(node),
      }
    })
    .filter((section) => section.title && section.categoryId)
}

const buildLegacyCategoryMegaMenu = (menuKey, sections) => {
  if (!Array.isArray(sections) || sections.length === 0) return null
  return {
    key: menuKey,
    fromAggregate: false,
    anchors: sections.map((section) => ({ key: section.key, label: section.title })),
    sections,
  }
}

const categoryMegaMenuMap = computed(() => {
  const roots = categoryStore.categoryList
  const map = {}

  const hotMenu = buildMegaMenuFromAggregatePage(aggregateMegaMenuPages.value.store, {
    menuKey: 'store',
    fallbackTitle: hotProductsNavLabel.value,
    resolveAllRoute: (section) => buildMegaMenuProductListRoute(section.categoryId),
  })
  if (hotMenu) {
    map.store = hotMenu
  } else if (!shouldUseAggregatePageMenu.value) {
    const hotSections = buildMegaMenuSections(roots, 'store-', (node) =>
      buildMegaMenuProductListRoute(node?.categoryId)
    ).map((section) => ({
      ...section,
      labelPick: HOT_PRODUCTS_FALLBACK_LABEL_PICK,
    }))
    const legacyHotMenu = buildLegacyCategoryMegaMenu('store', hotSections)
    if (legacyHotMenu) {
      map.store = legacyHotMenu
    }
  }

  topCategoryNavItems.value.forEach((item) => {
    const matchedRoot = item.category || categoryStore.findCategoryById(item.categoryId)
    const categoryMenu = buildPrimaryCategoryMegaMenu(matchedRoot, {
      menuKey: item.key,
      resolveAllRoute: (secondaryCategory) =>
        buildMegaMenuProductListRoute(matchedRoot?.categoryId, secondaryCategory.categoryId),
    })
    if (categoryMenu) {
      map[item.key] = categoryMenu
    }
  })

  return map
})

const currentMegaMenu = computed(() => {
  return categoryMegaMenuMap.value[activeCategoryMegaMenuKey.value] || null
})

const cachedCategoryMegaMenus = computed(() => {
  return cachedCategoryMegaMenuKeys.value
    .map((menuKey) => categoryMegaMenuMap.value[menuKey])
    .filter(Boolean)
})

/**
 * @description Resolve loading only for the active menu so hidden cached panels keep their DOM.
 * @param {string} menuKey - Mega-menu cache key.
 * @returns {boolean} Whether the keyed menu should render its skeleton.
 */
const isCategoryMegaMenuLoading = (menuKey) => {
  return resolveMegaMenuLoading({
    visible: showCategoryMegaMenu.value,
    menuKey,
    activeMenuKey: activeCategoryMegaMenuKey.value,
    aggregateLoading: aggregateMegaMenuLoading.value[menuKey],
    categoryLoading: categoryStore.loading,
    productsLoading: megaMenuProductsLoading.value[menuKey],
  })
}

const findTopCategoryKeyByRouteCategory = (targetId, items) => {
  if (!targetId) return ''
  const normalizedTargetId = String(targetId)

  const containsCategoryId = (nodes = []) => {
    return nodes.some((node) => {
      if (String(node.categoryId) === normalizedTargetId) {
        return true
      }
      return containsCategoryId(node.categories || [])
    })
  }

  const matchedItem = items.find((item) => {
    if (String(item.categoryId) === normalizedTargetId) {
      return true
    }
    return containsCategoryId(item.categories)
  })

  return matchedItem?.key || ''
}

// Derived auth state
const isLoggedIn = computed(() => isMounted.value && userStore.isLoggedIn)
const userInfo = computed(() => (isMounted.value ? userStore.userInfo : null))
/** @description 代理商和已认证工程商已具备合作身份，不展示“工程商认证”导航入口 */
const showJoinNav = computed(() => {
  if (!isMounted.value) return false
  if (userStore.isLoggedIn && !hasJoinNavigationIdentity(userInfo.value)) return false
  return shouldShowJoinNavigation(userInfo.value)
})
const isUserPanelVisible = computed(() => showUserPanel.value)
const isUserPanelMember = computed(() => isLoggedIn.value)
const {
  avatarUrl: userPanelAvatarUrl,
  nickname: userPanelDisplayName,
  phone: userPanelPhoneText,
} = useStoreAccountProfileDisplay(userInfo, { isReady: isMounted })
// const isAuthenticated = computed(() => {
//   if (!isMounted.value || !userInfo.value) return false
//   const userType = Number(userInfo.value?.userType)
//   return userType === 1 || userType === 2
// })

const activeKey = computed(() => {
  if (route.path === '/store') return 'store'
  if (route.path.startsWith('/store/category')) {
    return findTopCategoryKeyByRouteCategory(route.params.id, topCategoryNavItems.value) || 'store'
  }
  if (route.path.startsWith('/store/products')) {
    return (
      findTopCategoryKeyByRouteCategory(route.query.categoryId, topCategoryNavItems.value) ||
      'store'
    )
  }
  if (route.path.startsWith('/store')) return 'store'
  if (route.path.includes('solutions')) return 'solutions'
  if (route.path.includes('service')) return 'service'
  if (route.path.includes('join')) return 'join'
  // if (route.path.includes('cloud-platform')) return 'cloud-platform'
  return ''
})

// Login entry / account center redirect
const handleLogin = () => {
  // Logged-in users go to their order page.
  if (isLoggedIn.value) {
    navigateTo('/store/account/orders')
  } else {
    // Guests go to the login page.
    navigateToLogin()
    showUserPanel.value = false
  }
}

// Register entry
const handleRegister = () => {
  navigateToRegister()
  showUserPanel.value = false
}

// Navigate within the app
const navigateTo = (path) => {
  if (import.meta.client) {
    useRouter().push(path)
    showUserPanel.value = false
    closeCategoryMegaMenu()
  }
}

const isCategoryMegaMenuHoverLocked = (menuKey) => {
  return Boolean(menuKey && categoryMegaMenuHoverLockedKeys.value[menuKey])
}

const lockCategoryMegaMenuHover = (menuKey) => {
  if (!menuKey) return
  categoryMegaMenuHoverLockedKeys.value = {
    ...categoryMegaMenuHoverLockedKeys.value,
    [menuKey]: true,
  }
}

const unlockCategoryMegaMenuHover = (menuKey) => {
  if (!menuKey || !categoryMegaMenuHoverLockedKeys.value[menuKey]) {
    return
  }
  const next = { ...categoryMegaMenuHoverLockedKeys.value }
  delete next[menuKey]
  categoryMegaMenuHoverLockedKeys.value = next
}

const clearCategoryMegaMenuHideTimer = () => {
  if (categoryMegaMenuHideTimer) {
    clearTimeout(categoryMegaMenuHideTimer)
    categoryMegaMenuHideTimer = null
  }
}

const clearCategoryMegaMenuShowTimer = () => {
  if (categoryMegaMenuShowTimer) {
    clearTimeout(categoryMegaMenuShowTimer)
    categoryMegaMenuShowTimer = null
  }
  pendingCategoryMegaMenuKey = ''
}

const scheduleHideCategoryMegaMenu = () => {
  clearCategoryMegaMenuHideTimer()
  categoryMegaMenuHideTimer = setTimeout(() => {
    categoryMegaMenuHideTimer = null
    closeCategoryMegaMenu()
  }, CATEGORY_MEGA_MENU_HIDE_DELAY)
}

const setMegaMenuContentRef = (menuKey, element) => {
  if (!menuKey) return
  if (element) {
    megaMenuContentRefs.value[menuKey] = element
  } else {
    delete megaMenuContentRefs.value[menuKey]
  }
}

const setMegaMenuAnchorRef = (menuKey, sectionKey, element) => {
  if (!menuKey || !sectionKey) return
  if (element) {
    megaMenuAnchorRefs.value[menuKey] ||= {}
    megaMenuAnchorRefs.value[menuKey][sectionKey] = element
  } else {
    delete megaMenuAnchorRefs.value[menuKey]?.[sectionKey]
  }
}

const setMegaMenuBottomScrollArmed = (menuKey, armed) => {
  if (!menuKey || Boolean(megaMenuBottomScrollArmed.value[menuKey]) === Boolean(armed)) return
  megaMenuBottomScrollArmed.value = {
    ...megaMenuBottomScrollArmed.value,
    [menuKey]: Boolean(armed),
  }
}

const clearMegaMenuBottomScrollTimer = (timerMap, menuKey) => {
  const timer = timerMap.get(menuKey)
  if (!timer) return
  clearTimeout(timer)
  timerMap.delete(menuKey)
}

const resetMegaMenuBottomScrollState = (menuKey) => {
  if (!menuKey) return
  clearMegaMenuBottomScrollTimer(megaMenuBottomScrollArmTimers, menuKey)
  clearMegaMenuBottomScrollTimer(megaMenuBottomScrollCloseTimers, menuKey)
  setMegaMenuBottomScrollArmed(menuKey, false)
}

/**
 * @description Ignore scroll events caused by a sidebar anchor jump so they cannot arm closing.
 * @param {string} menuKey - Mega-menu cache key.
 * @returns {void}
 */
const suppressProgrammaticMegaMenuScrollArming = (menuKey) => {
  if (!menuKey) return
  resetMegaMenuBottomScrollState(menuKey)
  clearMegaMenuBottomScrollTimer(megaMenuProgrammaticScrollTimers, menuKey)
  megaMenuProgrammaticScrollKeys.add(menuKey)
  megaMenuProgrammaticScrollTimers.set(
    menuKey,
    setTimeout(() => {
      megaMenuProgrammaticScrollTimers.delete(menuKey)
      megaMenuProgrammaticScrollKeys.delete(menuKey)
    }, CATEGORY_MEGA_MENU_PROGRAMMATIC_SCROLL_GUARD_DELAY)
  )
}

const resetAllMegaMenuBottomScrollStates = () => {
  megaMenuBottomScrollArmTimers.forEach((timer) => clearTimeout(timer))
  megaMenuBottomScrollCloseTimers.forEach((timer) => clearTimeout(timer))
  megaMenuProgrammaticScrollTimers.forEach((timer) => clearTimeout(timer))
  megaMenuBottomScrollArmTimers.clear()
  megaMenuBottomScrollCloseTimers.clear()
  megaMenuProgrammaticScrollTimers.clear()
  megaMenuProgrammaticScrollKeys.clear()
  megaMenuBottomScrollArmed.value = {}
}

const scheduleMegaMenuBottomScrollArm = (menuKey) => {
  clearMegaMenuBottomScrollTimer(megaMenuBottomScrollArmTimers, menuKey)
  megaMenuBottomScrollArmTimers.set(
    menuKey,
    setTimeout(() => {
      megaMenuBottomScrollArmTimers.delete(menuKey)
      const container = megaMenuContentRefs.value[menuKey]
      if (
        !container ||
        !showCategoryMegaMenu.value ||
        activeCategoryMegaMenuKey.value !== menuKey
      ) {
        return
      }
      const boundary = resolveMegaMenuScrollBoundary({
        phase: 'scroll',
        scrollTop: container.scrollTop,
        scrollHeight: container.scrollHeight,
        clientHeight: container.clientHeight,
      })
      if (boundary.isAtBottom) {
        setMegaMenuBottomScrollArmed(menuKey, true)
      }
    }, MEGA_MENU_SCROLL_IDLE_CONFIRM_DELAY)
  )
}

const scheduleMegaMenuBottomScrollClose = (menuKey) => {
  clearMegaMenuBottomScrollTimer(megaMenuBottomScrollCloseTimers, menuKey)
  megaMenuBottomScrollCloseTimers.set(
    menuKey,
    setTimeout(() => {
      megaMenuBottomScrollCloseTimers.delete(menuKey)
      const container = megaMenuContentRefs.value[menuKey]
      if (
        !container ||
        !showCategoryMegaMenu.value ||
        activeCategoryMegaMenuKey.value !== menuKey
      ) {
        return
      }
      const boundary = resolveMegaMenuScrollBoundary({
        phase: 'wheel',
        scrollTop: container.scrollTop,
        scrollHeight: container.scrollHeight,
        clientHeight: container.clientHeight,
        deltaY: 1,
        armed: true,
      })
      if (boundary.shouldClose) {
        closeCategoryMegaMenu()
      }
    }, MEGA_MENU_SCROLL_CLOSE_DELAY)
  )
}

const setMegaMenuSectionRef = (menuKey, sectionKey, element) => {
  if (!menuKey || !sectionKey) return
  if (element) {
    megaMenuSectionRefs.value[menuKey] ||= {}
    megaMenuSectionRefs.value[menuKey][sectionKey] = element
  } else {
    delete megaMenuSectionRefs.value[menuKey]?.[sectionKey]
  }
}

const ensureCategoryTreeLoaded = async () => {
  if (categoryStore.hasCategories) return
  try {
    await categoryStore.fetchCategoryInfo()
  } catch (error) {
    console.error('[Header] Failed to load category tree for mega menu:', error)
  }
}

const ensureMegaMenuProductsLoaded = async (menu) => {
  if (!menu?.key || !Array.isArray(menu.sections) || menu.sections.length === 0) {
    return
  }
  if (megaMenuProductCache.value[menu.key] || megaMenuProductsLoading.value[menu.key]) {
    return
  }

  if (menu.fromAggregate) {
    megaMenuProductCache.value = {
      ...megaMenuProductCache.value,
      [menu.key]: menu.sections.reduce((acc, section) => {
        acc[section.key] = Array.isArray(section.products)
          ? section.products.slice(0, CATEGORY_MEGA_MENU_VISIBLE_CARD_COUNT)
          : []
        return acc
      }, {}),
    }
    return
  }

  const sectionProductMap = menu.sections.reduce((acc, section) => {
    if (Array.isArray(section.products) && section.products.length > 0) {
      acc[section.key] = section.products.slice(0, CATEGORY_MEGA_MENU_VISIBLE_CARD_COUNT)
    }
    return acc
  }, {})
  if (Object.keys(sectionProductMap).length === menu.sections.length) {
    megaMenuProductCache.value = {
      ...megaMenuProductCache.value,
      [menu.key]: sectionProductMap,
    }
    return
  }

  megaMenuProductsLoading.value = {
    ...megaMenuProductsLoading.value,
    [menu.key]: true,
  }

  try {
    const searchSections = menu.sections.filter((section) => section.loadFromSearch)
    if (searchSections.length > 0) {
      const entries = await Promise.all(
        searchSections.map(async (section) => {
          const response = await productApi.searchProducts(
            {
              current: 1,
              size: CATEGORY_MEGA_MENU_VISIBLE_CARD_COUNT,
              sort: 3,
              labelPick:
                Array.isArray(section.labelPick) && section.labelPick.length > 0
                  ? section.labelPick
                  : undefined,
              categoryId: section.categoryId,
            },
            { showError: false }
          )
          const records = Array.isArray(response?.records) ? response.records : []
          const preview = buildMegaMenuProductPreview(
            records,
            response?.total,
            CATEGORY_MEGA_MENU_VISIBLE_CARD_COUNT
          )
          return {
            key: section.key,
            products: preview.products,
            hasMore: preview.hasMore,
          }
        })
      )
      const resultByKey = new Map(entries.map((entry) => [entry.key, entry]))
      const nextSections = menu.sections.map((section) => {
        const entry = resultByKey.get(section.key)
        if (!entry) return section
        return {
          ...section,
          hasMore: entry.hasMore,
          products: entry.products,
        }
      })

      menu.sections = nextSections
      menu.anchors = nextSections.map((section) => ({
        key: section.key,
        label: section.title,
      }))

      megaMenuProductCache.value = {
        ...megaMenuProductCache.value,
        [menu.key]: nextSections.reduce((acc, section) => {
          acc[section.key] = section.products || []
          return acc
        }, {}),
      }
      return
    }

    const uniqueIds = [...new Set(menu.sections.flatMap((section) => section.productIds))].filter(
      Boolean
    )
    if (uniqueIds.length === 0) {
      megaMenuProductCache.value = {
        ...megaMenuProductCache.value,
        [menu.key]: {},
      }
      return
    }

    const response = await productApi.searchProducts(
      {
        current: 1,
        size: uniqueIds.length,
        prodIds: uniqueIds,
        sort: 2,
      },
      { showError: false }
    )

    const records = Array.isArray(response?.records) ? response.records : []
    const recordMap = new Map(records.map((item) => [String(item.prodId), item]))
    const sectionProductMap = menu.sections.reduce((acc, section) => {
      acc[section.key] = section.productIds
        .map((prodId) => recordMap.get(String(prodId)))
        .filter(Boolean)
        .slice(0, CATEGORY_MEGA_MENU_VISIBLE_CARD_COUNT)
      return acc
    }, {})

    megaMenuProductCache.value = {
      ...megaMenuProductCache.value,
      [menu.key]: sectionProductMap,
    }
  } catch (error) {
    megaMenuProductCache.value = {
      ...megaMenuProductCache.value,
      [menu.key]: {},
    }
  } finally {
    megaMenuProductsLoading.value = {
      ...megaMenuProductsLoading.value,
      [menu.key]: false,
    }
  }
}

const syncMegaMenuAnchorToSection = (menuKey = activeCategoryMegaMenuKey.value) => {
  if (!menuKey || menuKey !== activeCategoryMegaMenuKey.value) return
  const container = megaMenuContentRefs.value[menuKey]
  const sections = categoryMegaMenuMap.value[menuKey]?.sections || []
  if (!container || sections.length === 0) return

  const topThreshold = CATEGORY_MEGA_MENU_SCROLL_OFFSET
  let activeSectionKey = sections[0].key
  sections.forEach((section) => {
    const element = megaMenuSectionRefs.value[menuKey]?.[section.key]
    if (!element) return
    if (element.offsetTop - container.scrollTop <= topThreshold) {
      activeSectionKey = section.key
    }
  })
  activeMegaMenuAnchorKey.value = activeSectionKey
}

const scrollToMegaMenuSection = (menuKey, sectionKey, behavior = 'smooth') => {
  const container = megaMenuContentRefs.value[menuKey]
  const target = megaMenuSectionRefs.value[menuKey]?.[sectionKey]
  if (!container || !target) return

  suppressProgrammaticMegaMenuScrollArming(menuKey)
  container.scrollTo({
    top: Math.max(target.offsetTop - CATEGORY_MEGA_MENU_SCROLL_OFFSET, 0),
    behavior,
  })
}

/**
 * @description Keep the active category anchor inside the independently scrollable sidebar.
 * @param {string} menuKey - Mega-menu cache key.
 * @param {string} sectionKey - Active section key.
 * @param {ScrollBehavior} behavior - Sidebar scroll behavior.
 * @returns {void}
 */
const scrollMegaMenuAnchorIntoView = (menuKey, sectionKey, behavior = 'smooth') => {
  if (!import.meta.client || !menuKey || !sectionKey) return
  const target = megaMenuAnchorRefs.value[menuKey]?.[sectionKey]
  const container = target?.closest('.category-mega-menu__sidebar')
  if (!target || !container) return

  const containerRect = container.getBoundingClientRect()
  const targetRect = target.getBoundingClientRect()
  let nextScrollTop = container.scrollTop

  if (targetRect.top < containerRect.top) {
    nextScrollTop += targetRect.top - containerRect.top
  } else if (targetRect.bottom > containerRect.bottom) {
    nextScrollTop += targetRect.bottom - containerRect.bottom
  } else {
    return
  }

  container.scrollTo({
    top: Math.max(nextScrollTop, 0),
    behavior,
  })
}

watch([activeCategoryMegaMenuKey, activeMegaMenuAnchorKey], async ([menuKey, sectionKey]) => {
  if (!showCategoryMegaMenu.value || !menuKey || !sectionKey) return
  await nextTick()
  scrollMegaMenuAnchorIntoView(menuKey, sectionKey)
})

const openCategoryMegaMenu = async (menuKey) => {
  if (isMobile.value || isCategoryMegaMenuHoverLocked(menuKey)) return
  if (showCategoryMegaMenu.value && activeCategoryMegaMenuKey.value === menuKey) {
    clearCategoryMegaMenuHideTimer()
    return
  }
  const requestToken = ++categoryMegaMenuRequestToken
  const isReopening = !showCategoryMegaMenu.value
  const isSwitchingMenu =
    showCategoryMegaMenu.value &&
    Boolean(activeCategoryMegaMenuKey.value) &&
    activeCategoryMegaMenuKey.value !== menuKey
  clearCategoryMegaMenuHideTimer()
  beginMegaMenuHoverTiming(menuKey)
  showCategoryMegaMenu.value = true
  activeCategoryMegaMenuKey.value = menuKey
  resetMegaMenuBottomScrollState(menuKey)
  const isFirstActivation = !cachedCategoryMegaMenuKeys.value.includes(menuKey)
  if (isFirstActivation) {
    cachedCategoryMegaMenuKeys.value = [...cachedCategoryMegaMenuKeys.value, menuKey]
  }
  const shouldResetContentScroll = isFirstActivation || isReopening || isSwitchingMenu
  if (shouldResetContentScroll) {
    await nextTick()
    const cachedContentElement = megaMenuContentRefs.value[menuKey]
    if (cachedContentElement) {
      cachedContentElement.scrollTop = 0
    }
  }

  if (menuKey === 'store') {
    await fetchHotProductsAggregatePage()
  }
  await ensureCategoryTreeLoaded()
  if (
    requestToken !== categoryMegaMenuRequestToken ||
    activeCategoryMegaMenuKey.value !== menuKey
  ) {
    return
  }
  const menu = categoryMegaMenuMap.value[menuKey]
  if (!menu) return

  activeMegaMenuAnchorKey.value = menu.anchors[0]?.key || ''
  await ensureMegaMenuProductsLoaded(menu)
  if (
    requestToken !== categoryMegaMenuRequestToken ||
    activeCategoryMegaMenuKey.value !== menuKey
  ) {
    return
  }

  activeMegaMenuAnchorKey.value = menu.anchors[0]?.key || ''
  await nextTick()
  const contentElement = megaMenuContentRefs.value[menuKey]
  if (shouldResetContentScroll && contentElement) {
    contentElement.scrollTop = 0
  } else {
    syncMegaMenuAnchorToSection(menuKey)
  }
  trackMegaMenuHoverExposure(menuKey)
  initMegaMenuProductExposure(menuKey)
}

const closeCategoryMegaMenu = () => {
  categoryMegaMenuRequestToken += 1
  flushMegaMenuHoverDuration(activeCategoryMegaMenuKey.value)
  resetMegaMenuBottomScrollState(activeCategoryMegaMenuKey.value)
  clearCategoryMegaMenuShowTimer()
  clearCategoryMegaMenuHideTimer()
  showCategoryMegaMenu.value = false
  hoveredMegaMenuAnchorKey.value = ''
}

const handleCategoryMegaMenuAfterLeave = () => {
  if (showCategoryMegaMenu.value) return
  activeCategoryMegaMenuKey.value = ''
  activeMegaMenuAnchorKey.value = ''
}

const handleCategoryNavWheel = (event) => {
  if (!showCategoryMegaMenu.value || Number(event.deltaY) === 0) return
  event.preventDefault()
  event.stopPropagation()
  closeCategoryMegaMenu()
}

const handleCategoryNavMouseEnter = (menuKey) => {
  if (isCategoryMegaMenuHoverLocked(menuKey)) {
    return
  }
  clearCategoryMegaMenuHideTimer()

  if (showCategoryMegaMenu.value) {
    clearCategoryMegaMenuShowTimer()
    openCategoryMegaMenu(menuKey)
    return
  }

  if (pendingCategoryMegaMenuKey === menuKey && categoryMegaMenuShowTimer) {
    return
  }

  clearCategoryMegaMenuShowTimer()
  pendingCategoryMegaMenuKey = menuKey
  categoryMegaMenuShowTimer = setTimeout(() => {
    const pendingMenuKey = pendingCategoryMegaMenuKey
    categoryMegaMenuShowTimer = null
    pendingCategoryMegaMenuKey = ''
    if (pendingMenuKey) {
      openCategoryMegaMenu(pendingMenuKey)
    }
  }, CATEGORY_MEGA_MENU_SHOW_DELAY)
}

const handleStoreNavClick = () => {
  lockCategoryMegaMenuHover('store')
  closeCategoryMegaMenu()
  navigateTo('/store')
}

const handleCategoryNavLinkClick = (menuKey) => {
  lockCategoryMegaMenuHover(menuKey)
  closeCategoryMegaMenu()
}

const handleCategoryMegaMenuCloseClick = () => {
  if (activeCategoryMegaMenuKey.value) {
    lockCategoryMegaMenuHover(activeCategoryMegaMenuKey.value)
  }
  closeCategoryMegaMenu()
}

const handleCategoryNavTriggerMouseLeave = (menuKey, event) => {
  const relatedTarget = event.relatedTarget
  if (relatedTarget && relatedTarget.closest('.category-mega-menu-layer')) {
    return
  }
  clearCategoryMegaMenuShowTimer()
  unlockCategoryMegaMenuHover(menuKey)
  scheduleHideCategoryMegaMenu()
}

const handleCategoryMegaMenuMouseEnter = () => {
  clearCategoryMegaMenuHideTimer()
}

const handleCategoryMegaMenuMouseLeave = (event) => {
  const relatedTarget = event.relatedTarget
  if (relatedTarget && relatedTarget.closest('.base-header__menu-item-wrapper')) {
    return
  }
  hoveredMegaMenuAnchorKey.value = ''
  scheduleHideCategoryMegaMenu()
}

const handleCategoryMegaMenuDismissZoneEnter = () => {
  hoveredMegaMenuAnchorKey.value = ''
  scheduleHideCategoryMegaMenu()
  if (activeCategoryMegaMenuKey.value) {
    unlockCategoryMegaMenuHover(activeCategoryMegaMenuKey.value)
  }
}

const handleMegaMenuAnchorHover = (menuKey, sectionKey) => {
  hoveredMegaMenuAnchorKey.value = sectionKey
  activeMegaMenuAnchorKey.value = sectionKey
  scrollToMegaMenuSection(menuKey, sectionKey, 'auto')
}

const handleMegaMenuAnchorLeave = () => {
  hoveredMegaMenuAnchorKey.value = ''
}

const handleMegaMenuAnchorClick = (menuKey, sectionKey) => {
  hoveredMegaMenuAnchorKey.value = sectionKey
  activeMegaMenuAnchorKey.value = sectionKey
  scrollToMegaMenuSection(menuKey, sectionKey, 'auto')
  const menu = categoryMegaMenuMap.value[menuKey]
  const section = menu?.sections?.find((item) => item.key === sectionKey)
  if (section?.title) {
    const prefix = resolveMegaMenuPrefix(menuKey, topCategoryNavItems.value)
    trackClickEvent({
      pvEvent: `pv_${prefix}_Hover_Category_click`,
      targetType: 'mega_menu_category',
      targetId: String(section.categoryId || sectionKey),
      targetName: section.title,
      pageName: `${prefix}悬浮页`,
      properties: {
        category_id: String(section.categoryId || ''),
        category_name: section.title,
      },
    })
  }
}

const handleMegaMenuContentScroll = (menuKey) => {
  syncMegaMenuAnchorToSection(menuKey)
  const container = megaMenuContentRefs.value[menuKey]
  if (!container) return

  const maxScroll = container.scrollHeight - container.clientHeight
  const shortPage = !(Number.isFinite(maxScroll) && maxScroll > 0)
  const percent = calcScrollPercent(container.scrollTop, maxScroll)
  trackMegaMenuScrollDepthByPercent(menuKey, percent, { shortPage })

  if (megaMenuProgrammaticScrollKeys.has(menuKey)) {
    resetMegaMenuBottomScrollState(menuKey)
    return
  }
  const boundary = resolveMegaMenuScrollBoundary({
    phase: 'scroll',
    scrollTop: container.scrollTop,
    scrollHeight: container.scrollHeight,
    clientHeight: container.clientHeight,
    armed: megaMenuBottomScrollArmed.value[menuKey],
  })
  if (!boundary.isAtBottom) {
    resetMegaMenuBottomScrollState(menuKey)
    return
  }
  if (!boundary.armed) {
    scheduleMegaMenuBottomScrollArm(menuKey)
  }
}

const handleMegaMenuContentWheel = (menuKey, event) => {
  if (menuKey !== activeCategoryMegaMenuKey.value) return
  const container = event.currentTarget
  const boundary = resolveMegaMenuScrollBoundary({
    phase: 'wheel',
    scrollTop: container.scrollTop,
    scrollHeight: container.scrollHeight,
    clientHeight: container.clientHeight,
    deltaY: event.deltaY,
    armed: megaMenuBottomScrollArmed.value[menuKey],
  })
  event.stopPropagation()
  if (!boundary.isAtBottom || event.deltaY < 0) {
    resetMegaMenuBottomScrollState(menuKey)
    return
  }
  if (event.deltaY === 0) return
  event.preventDefault()
  if (boundary.shouldClose) {
    scheduleMegaMenuBottomScrollClose(menuKey)
  } else {
    scheduleMegaMenuBottomScrollArm(menuKey)
  }
}

const isMegaMenuNavActive = (menuKey) => {
  return showCategoryMegaMenu.value && activeCategoryMegaMenuKey.value === menuKey
}

const getMegaMenuSectionProducts = (menuKey, sectionKey) => {
  if (!menuKey) return []
  return megaMenuProductCache.value[menuKey]?.[sectionKey] || []
}

const getMegaMenuSectionEntries = (menuKey, section) => {
  const productEntries = getMegaMenuSectionProducts(menuKey, section.key).map((product) => ({
    type: 'product',
    key: `product-${product.prodId}`,
    product,
  }))

  if (section?.hasMore && section?.allRoute) {
    productEntries.push({
      type: 'all',
      key: `${section.key}-all`,
    })
  }

  return productEntries
}

const handleMegaMenuViewAll = (section) => {
  if (!section?.allRoute) return
  const menuKey = activeCategoryMegaMenuKey.value
  const prefix = resolveMegaMenuPrefix(menuKey, topCategoryNavItems.value)
  trackClickEvent({
    pvEvent: `pv_${prefix}_Hover_All_click`,
    targetType: 'mega_menu_view_all',
    targetId: String(section.categoryId || section.key || ''),
    targetName: section.title || '',
    pageName: `${prefix}悬浮页`,
    properties: {
      category_id: String(section.categoryId || ''),
      category_name: section.title || '',
    },
  })
  navigateTo(section.allRoute)
}

/**
 * @description Go to the account center.
 */
const handleGoToAccountCenter = () => {
  navigateTo('/store/account')
}

// // Open the cloud platform in a new page
// const handleCloudPlatform = () => {
//   if (!import.meta.client) return
//
//   const config = useRuntimeConfig()
//   const refreshToken = userStore.refreshToken
//   const baseUrl = resolveImsBaseUrl(config.public)
//   const url = refreshToken
//     ? `${baseUrl}?refresh_token=${encodeURIComponent(refreshToken)}`
//     : baseUrl
//
//   openExternal(url)
// }
//
// // Mobile entry for the cloud platform
// const handleCloudPlatformMobile = () => {
//   closeMenu()
//   handleCloudPlatform()
// }

const toCart = () => {
  openExternal('/store/cart')
}

const handleCart = () => {
  if (!isLoggedIn.value) {
    navigateToLogin()
    showUserPanel.value = false
    return
  }
  if (import.meta.client) {
    if (isMobile.value) {
      closeMobileHeaderOverlayUI()
    }
    showUserPanel.value = false
    toCart()
  }
}

const handleCartCheckout = () => {
  if (import.meta.client) {
    showCartPanel.value = false
    toCart()
  }
}

const handleCartGoShopping = () => {
  showCartPanel.value = false
  navigateTo('/store/products')
}

const clearCartPanelHideTimer = () => {
  if (cartPanelTimeout) {
    clearTimeout(cartPanelTimeout)
    cartPanelTimeout = null
  }
}

const scheduleHideCartPanel = () => {
  clearCartPanelHideTimer()
  cartPanelTimeout = setTimeout(() => {
    showCartPanel.value = false
    cartPanelTimeout = null
  }, USER_PANEL_HIDE_DELAY)
}

const handleCartButtonClick = () => {
  if (!isLoggedIn.value) {
    navigateToLogin()
    return
  }
  if (import.meta.client) {
    showCartPanel.value = false
    toCart()
  }
}

const handleCartButtonMouseEnter = () => {
  if (isMobile.value) return
  clearCartPanelHideTimer()
  showCartPanel.value = true
}

const handleCartButtonMouseLeave = (event) => {
  if (isMobile.value) return
  const relatedTarget = event.relatedTarget
  if (relatedTarget && relatedTarget.closest('.cart-panel-wrapper')) return
  scheduleHideCartPanel()
}

const handleCartPanelMouseEnter = () => {
  if (isMobile.value) return
  clearCartPanelHideTimer()
  showCartPanel.value = true
}

const handleCartPanelMouseLeave = (event) => {
  if (isMobile.value) return
  const relatedTarget = event.relatedTarget
  if (relatedTarget && relatedTarget.closest('.cart-button-wrapper')) return
  scheduleHideCartPanel()
}

// Logout
const handleLogout = () => {
  if (!import.meta.client) return

  CommonModal.confirm({
    title: '确认退出',
    content: '确定要退出登录吗？',
    type: 'warning',
    okText: '确定',
    cancelText: '取消',
    onOk: async () => {
      showUserPanel.value = false // Close the user panel immediately after confirmation.
      try {
        // userStore.logout handles the logout API request.
        await userStore.logout()
      } catch (error) {
        // Even if the API fails, still clear local auth state.
        console.error('退出登录失败:', error)
        await userStore.logout(true) // Skip the API call and clear local state directly.
      } finally {
        BaseMessage.success('已退出登录')
        setTimeout(() => {
          navigateTo('/')
        }, 500)
      }
    },
  })
}

/**
 * @description Clear the delayed user panel hide timer.
 * @returns {void}
 */
const clearUserPanelHideTimer = () => {
  if (userPanelTimeout) {
    clearTimeout(userPanelTimeout)
    userPanelTimeout = null
  }
}

/**
 * @description Hide the user panel with a short delay to avoid hover flicker.
 * @returns {void}
 */
const scheduleHideUserPanel = () => {
  clearUserPanelHideTimer()
  userPanelTimeout = setTimeout(() => {
    showUserPanel.value = false
    userPanelTimeout = null
  }, USER_PANEL_HIDE_DELAY)
}

/**
 * @description Handle mouse enter on the user button.
 */
const handleUserButtonClick = () => {
  if (isLoggedIn.value) {
    clearUserPanelHideTimer()
    if (isMobile.value && !showUserPanel.value) {
      closeMenu()
      showSearchPanel.value = false
      handleLanguagePanelClose()
    }
    showUserPanel.value = !showUserPanel.value
  } else {
    navigateToLogin()
    showUserPanel.value = false
  }
}

const handleUserButtonMouseEnter = () => {
  if (isMobile.value) return
  clearUserPanelHideTimer()
  showUserPanel.value = true
}

/**
 * @description Handle mouse leave on the user button.
 * @param {MouseEvent} event - Mouse event object
 */
const handleUserButtonMouseLeave = (event) => {
  if (isMobile.value) return
  // Ignore the leave event when the pointer moves into the panel.
  const relatedTarget = event.relatedTarget

  if (relatedTarget && relatedTarget.closest('.user-panel-wrapper')) {
    return
  }

  // Delay the hide so the pointer can move into the panel safely.
  scheduleHideUserPanel()
}

/**
 * @description Handle mouse enter on the user panel.
 */
const handleUserPanelMouseEnter = () => {
  if (isMobile.value) return
  clearUserPanelHideTimer()
  showUserPanel.value = true
}

/**
 * @description Handle mouse leave on the user panel.
 * @param {MouseEvent} event - Mouse event object
 */
const handleUserPanelMouseLeave = (event) => {
  if (isMobile.value) return
  // Ignore the leave event when the pointer moves back to the button.
  const relatedTarget = event.relatedTarget

  if (relatedTarget && relatedTarget.closest('.user-button-wrapper')) {
    return
  }

  // Otherwise hide the panel with the shared delay.
  scheduleHideUserPanel()
}

/**
 * @description 移动端：收起侧栏菜单、搜索浮层、语言抽屉、用户下拉
 * @returns {void}
 */
const closeMobileHeaderOverlayUI = () => {
  if (!isMobile.value) return
  closeMenu()
  showSearchPanel.value = false
  handleLanguagePanelClose()
  showUserPanel.value = false
  clearUserPanelHideTimer()
}

const handleMobileDocumentPointerDown = (event) => {
  if (!isMobile.value || !showUserPanel.value) return
  const target = event?.target
  const clickRoot = target instanceof Element ? target : target?.parentElement
  if (!clickRoot) return
  if (clickRoot.closest('.user-button-wrapper')) return

  showUserPanel.value = false
  clearUserPanelHideTimer()
}

const toggleMenu = () => {
  if (isMobile.value && !isMenuOpen.value) {
    showSearchPanel.value = false
    handleLanguagePanelClose()
    showUserPanel.value = false
    clearUserPanelHideTimer()
    mobileExpandedMenuKeys.value = []
  }
  isMenuOpen.value = !isMenuOpen.value
}

const closeMenu = () => {
  isMenuOpen.value = false
}

/**
 * @description 移动端：点击顶栏 Logo/空白处时收起所有头部浮层（各操作按钮使用 @click.stop 避免误关）
 * @param {MouseEvent} event
 * @returns {void}
 */
const handleMobileNavBarClick = (event) => {
  if (!isMobile.value) return
  const target = event?.target
  const clickRoot = target instanceof Element ? target : target?.parentElement
  if (!clickRoot) return
  if (clickRoot.closest('.base-header__mobile-menu-btn')) return
  if (clickRoot.closest('.base-header__action-search')) return
  if (clickRoot.closest('.base-header__action-lang')) return
  if (clickRoot.closest('.user-button-wrapper')) return
  if (clickRoot.closest('[aria-label="Cart"]')) return
  closeMobileHeaderOverlayUI()
}

// ========== Search panel state ==========
const showSearchPanel = ref(false) // Controls search panel visibility

/**
 * @description Toggle the search panel.
 */
const toggleSearchPanel = () => {
  if (isMobile.value && !showSearchPanel.value) {
    closeMenu()
    handleLanguagePanelClose()
    showUserPanel.value = false
    clearUserPanelHideTimer()
  }
  showSearchPanel.value = !showSearchPanel.value
}

/**
 * @description 移动端：打开语言选择抽屉，并收起其它头部浮层
 * @returns {void}
 */
const openMobileLanguageSheet = () => {
  if (isMobile.value) {
    closeMenu()
    showSearchPanel.value = false
    showUserPanel.value = false
    clearUserPanelHideTimer()
  }
  languageDropdownVisible.value = true
}

/**
 * @description Handle the search action.
 * @param {string} keyword - Search keyword
 */
const handleSearch = (keyword) => {
  // Navigate to the search result page.
  navigateTo(`/store/search?q=${encodeURIComponent(keyword)}`)
}

// Header overlay scroll locking
const syncHeaderOverlayScrollLock = (shouldLock) => {
  if (!import.meta.client) return

  if (shouldLock && !hasHeaderOverlayScrollLock) {
    lockBodyScroll()
    hasHeaderOverlayScrollLock = true
    return
  }

  if (!shouldLock && hasHeaderOverlayScrollLock) {
    unlockBodyScroll()
    hasHeaderOverlayScrollLock = false
  }
}

watch(shouldLockHeaderOverlayScroll, syncHeaderOverlayScrollLock)

watch(
  () => languageDropdownVisible.value || isLanguageInputFocused.value,
  (val) => {
    if (!val) {
      languagePanelRef.value?.resetSearch()
    }
  }
)

watch(
  () => userStore.isLoggedIn,
  (isLoggedIn) => {
    if (isLoggedIn && import.meta.client) {
      cartStore.fetchHeaderCart(0).catch(() => {})
    }
  },
  { immediate: true }
)

watch(showCartPanel, (visible) => {
  if (visible && isLoggedIn.value && import.meta.client) {
    // The hover cart panel is not modal, so do not lock page scrolling.
    cartStore.fetchHeaderCart(0).catch(() => {})
  }
})

onMounted(() => {
  isHeaderUnmounted = false
  // Delay the mounted flag to keep SSR and client markup aligned.
  nextTick(() => {
    window.__TENDA_HEADER_CLIENT_READY = true
    isMounted.value = true
  })

  if (import.meta.client) {
    // Refresh user info after mount so the client state stays in sync.
    // auth-callback.js also participates in user state synchronization.
    if (userStore.isLoggedIn) {
      getUserInfo()
    }
    document.addEventListener('pointerdown', handleMobileDocumentPointerDown, true)
  }
})

onUnmounted(() => {
  if (import.meta.client) {
    document.removeEventListener('pointerdown', handleMobileDocumentPointerDown, true)
    syncHeaderOverlayScrollLock(false)
  }
  flushMegaMenuHoverDuration(activeCategoryMegaMenuKey.value || megaMenuHoverTiming.value?.menuKey)
  clearLanguageInputBlurTimer()
  clearUserPanelHideTimer()
  clearCartPanelHideTimer()
  clearCategoryMegaMenuShowTimer()
  clearCategoryMegaMenuHideTimer()
  resetAllMegaMenuBottomScrollStates()
  megaMenuViewportExposure.disconnect()
  isHeaderUnmounted = true
  gsapInstance?.killTweensOf('.mobile-menu')
})

// GSAP animations
const onBeforeEnter = (el) => {
  el.style.opacity = '0'
  el.style.height = '0'
  el.style.overflow = 'hidden'
}

const onEnter = (el, done) => {
  void loadGsap()
    .then((gsap) => {
      if (!gsap || isHeaderUnmounted) {
        done()
        return
      }
      gsap.set(el, { height: '', opacity: 1 })
      const targetHeight = getComputedStyle(el).height
      gsap.set(el, { height: 0, opacity: 0 })
      gsap.to(el, {
        opacity: 1,
        height: targetHeight,
        duration: 0.3,
        ease: 'power2.out',
        onComplete: () => {
          gsap.set(el, { height: '', overflow: '' })
          done()
        },
      })
    })
    .catch(() => done())
}

const onLeave = (el, done) => {
  void loadGsap()
    .then((gsap) => {
      if (!gsap || isHeaderUnmounted) {
        done()
        return
      }
      gsap.set(el, { overflow: 'hidden', backgroundColor: 'var(--ip-bg-surface)' })
      const currentHeight = getComputedStyle(el).height
      gsap.set(el, { height: currentHeight })
      gsap.to(el, {
        opacity: 0,
        height: 0,
        duration: 0.3,
        ease: 'power2.in',
        onComplete: done,
      })
    })
    .catch(() => done())
}

const customHeaderSurfaceStyle = {
  background: 'var(--ip-bg-surface)',
}
</script>

<style lang="scss" scoped>
@use 'sass:math';

$category-mega-menu-bottom-overlay-height: clamp(120px, calc(42.8571vh - 222.857px), 240px);
$category-mega-menu-panel-height: calc(
  100vh - #{$base-header-height} - #{$category-mega-menu-bottom-overlay-height}
);

/* Header Styles */
.base-header {
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s;

  :deep(.arco-badge-number) {
    box-shadow: none;
  }

  &__navbar {
    height: $base-header-height;
    background-color: $bg-white;
    border-bottom: 1px solid $border-light-color;

    > .base-container {
      height: 100%;
    }

    @include respond-to-max('lg') {
      height: $base-header-height-mobile;

      > .base-container {
        padding-left: 0;
        padding-right: 0;
      }
    }
  }

  /* ---------- Desktop main navigation ---------- */
  &__navbar-pc {
    height: 100%;

    @include respond-to-max('lg') {
      display: none;
    }

    &-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 100%;
    }
  }

  &__logo {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    height: 100%;

    a {
      display: flex;
      align-items: center;
      flex: 0 0 auto;
      height: 100%;
      text-decoration: none;
      border: 0;
      outline: none;

      &:hover,
      &:active {
        border: 0;
        outline: none;
        box-shadow: none;
      }

      &:focus:not(:focus-visible) {
        border: 0;
        outline: none;
        box-shadow: none;
      }

      &:focus-visible {
        border: 0;
        border-radius: 4px;
        outline: 2px solid rgba($primary-color, 0.55);
        outline-offset: 4px;
        box-shadow: none;
      }

      @supports not selector(:focus-visible) {
        &:focus {
          border: 0;
          border-radius: 4px;
          outline: 2px solid rgba($primary-color, 0.55);
          outline-offset: 4px;
          box-shadow: none;
        }
      }
    }

    .logo-img {
      display: block;
      flex: 0 0 auto;
      width: 84px;
      height: 24px;
      max-width: none;
      object-fit: contain;
    }
  }

  &__nav-wrapper {
    flex: 1;
    display: flex;
    justify-content: flex-start;
    overflow: hidden;
    visibility: visible;
  }

  &__menu {
    margin-left: 24px;
    display: flex;
    align-items: center;
    gap: 0;
  }

  &__menu--skeleton {
    gap: 24px;
    min-width: 320px;
    height: $base-header-height;
    pointer-events: none;
  }

  &__menu-skeleton-item {
    display: block;
    width: auto;

    :deep(.arco-skeleton-line-row) {
      height: 20px;
      border-radius: 999px;
      margin: 0;
    }
  }

  &__menu-item-wrapper {
    position: relative;
    display: flex;
    align-items: stretch;
    height: $base-header-height;
  }

  &__menu-item {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: $base-header-height;
    padding: 0 16px;
    border: none;
    background: transparent;
    color: $text-title-color;
    font-size: 16px;
    line-height: 24px;
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover,
    &.is-active {
      color: $primary-color;
    }

    @include respond-to-max('xl') {
      padding: 0 12px;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    height: 100%;
    flex-shrink: 0;

    .action-btn {
      color: $text-title-color;
      font-size: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-decoration: none;
      width: $spacing-xs * 5; // 40px
      height: $spacing-xs * 5; // 40px
      padding: 0;
      border-radius: $radius-sm; // 4px
      transition: all 0.2s ease;
      cursor: pointer;

      &:hover {
        background-color: rgba($color-black, 0.06);
      }

      &--active {
        // background-color: rgba($primary-color, 0.08);
        // color: $primary-color;
        background-color: rgba($color-black, 0.06);
      }

      // Badge styling
      :deep(.arco-badge) {
        .arco-badge-number {
          background-color: $primary-color;
          color: $bg-white;
          font-size: 10px;
          font-weight: 500;
          min-width: 18px;
          height: 18px;
          line-height: 18px;
          padding: 0 6px;
          border-radius: 9px;
        }
      }

      // Support both root-level and nested Arco button class structures.
      &.arco-btn,
      :deep(.arco-btn) {
        width: $spacing-xs * 4; // 40px
        height: $spacing-xs * 4; // 40px
        padding: 0;
        border-radius: $radius-sm; // 4px
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &.arco-btn:hover,
      &.arco-btn:focus-visible,
      :deep(.arco-btn:hover),
      :deep(.arco-btn:focus-visible) {
        background-color: rgba($color-black, 0.06);
      }

      &.arco-btn.action-btn--active,
      :deep(.arco-btn.action-btn--active) {
        background-color: rgba($color-black, 0.06);
      }
    }

    .user-action-icon {
      position: relative;
      width: $spacing-xs * 3;
      height: $spacing-xs * 3;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      &__status {
        position: absolute;
        right: 1px;
        bottom: 1px;
        width: $spacing-xs - math.div($spacing-2xs, 2);
        height: $spacing-xs - math.div($spacing-2xs, 2);
        background-color: $success-color;
        border: math.div($spacing-2xs, 4) solid $bg-white;
        border-radius: 50%;
        box-sizing: border-box;
        pointer-events: none;
      }
    }
  }

  &__actions-fallback {
    display: flex;
    align-items: center;
    gap: 8px;

    .action-btn {
      border: none;
      background: transparent;
    }

    &--mobile {
      gap: 4px;
    }
  }

  /* ---------- Mobile main navigation ---------- */
  &__navbar-mobile {
    height: 100%;
    display: none;
    background-color: transparent;

    @include respond-to-max('lg') {
      display: block;
    }

    &-inner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 100%;
      padding: 0 8px;
    }
  }

  &__navbar-mobile &__navbar-mobile-left {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  &__navbar-mobile &__logo {
    flex: 1;
    justify-content: flex-start;
    margin-left: 8px;
  }

  &__navbar-mobile .logo-img {
    width: 84px;
    height: 24px;
  }

  &__navbar-mobile &__actions .action-btn {
    min-width: 32px;
    min-height: 32px;
    width: 32px;
    height: 32px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__lang-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: $text-title-color;

    :deep(.svg-icon-container) {
      color: inherit;
    }
  }

  &__navbar-mobile &__actions :deep(.arco-badge .arco-badge-number) {
    font-size: 10px;
    font-weight: 400;
    min-width: 14px;
    height: 14px;
    line-height: 14px;
    padding: 0 4px;
    border-radius: 7px;
  }

  &__mobile-menu-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    margin: 0;
    padding: 0;
    color: $text-title-color;
    border-radius: $radius-sm;
    transition: all 0.2s;

    :deep(.arco-icon) {
      font-size: 20px;
    }

    &--fallback {
      border: none;
      background: transparent;
    }
  }

  &__mobile-menu-icon {
    position: relative;
    display: inline-block;
    width: 18px;
    height: 2px;
    background-color: currentColor;
    border-radius: 999px;

    &::before,
    &::after {
      content: '';
      position: absolute;
      left: 0;
      width: 18px;
      height: 2px;
      background-color: currentColor;
      border-radius: 999px;
    }

    &::before {
      top: -6px;
    }

    &::after {
      top: 6px;
    }
  }
}

.category-mega-menu-layer {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  height: calc(100vh - #{$base-header-height});
  background: rgba($bg-white, 0.5);
  backdrop-filter: blur(6px);
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 0;
    height: $category-mega-menu-panel-height;
    background: $bg-color;
    box-shadow: 0 4px 16px rgba($color-black, 0.12);
    pointer-events: none;
  }

  &__dismiss-zone {
    position: absolute;
    left: 0;
    right: 0;
    top: $category-mega-menu-panel-height;
    bottom: 0;
    z-index: 0;
  }
}

.category-mega-menu-drop-enter-active {
  transition: opacity 0.2s cubic-bezier(0.4, 0, 0.6, 1);

  &::after {
    transition: height 0.3s cubic-bezier(0.33, 0, 0.67, 1);
  }

  .category-mega-menu {
    transition: clip-path 0.3s cubic-bezier(0.33, 0, 0.67, 1);
  }

  .category-mega-menu__inner {
    transition:
      opacity 0.25s 0.15s cubic-bezier(0.4, 0, 0.6, 1),
      transform 0.25s 0.15s cubic-bezier(0.4, 0, 0.6, 1);
  }
}

.category-mega-menu-drop-leave-active {
  transition: opacity 0.2s 0.15s cubic-bezier(0.4, 0, 0.6, 1);

  &::after {
    transition: height 0.25s 0.15s cubic-bezier(0.4, 0, 0.6, 1);
  }

  .category-mega-menu {
    transition: clip-path 0.25s 0.15s cubic-bezier(0.4, 0, 0.6, 1);
  }

  .category-mega-menu__inner {
    transition:
      opacity 0.25s cubic-bezier(0.4, 0, 0.6, 1),
      transform 0.25s cubic-bezier(0.4, 0, 0.6, 1);
  }
}

.category-mega-menu-drop-enter-from,
.category-mega-menu-drop-leave-to {
  opacity: 0;

  &::after {
    height: 0;
  }

  .category-mega-menu {
    clip-path: inset(0 0 100% 0);
  }

  .category-mega-menu__inner {
    opacity: 0;
    transform: translateY(-40px);
  }
}

.category-mega-menu {
  position: relative;
  z-index: 1;
  height: $category-mega-menu-panel-height;
  min-height: 0;
  clip-path: inset(0 0 0 0);
  padding: 0;
  box-sizing: border-box;
  background: $bg-color;

  &__inner {
    display: grid;
    grid-template-columns: 244px minmax(0, 1fr);
    gap: 16px;
    align-items: stretch;
    height: 100%;
    box-sizing: border-box;
  }

  &__sidebar {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 48px;
    padding-bottom: 24px;
    align-self: stretch;
    height: 100%;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior-y: contain;
    box-sizing: border-box;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__anchor {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 220px;
    min-height: 48px;
    margin: 0 12px;
    padding: 12px 16px;
    min-width: 0;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: $text-title-color;
    text-align: left;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover,
    &.is-active {
      background: rgba(238, 238, 238, 0.93);
    }
  }

  &__anchor-text {
    display: block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 16px;
    line-height: 24px;
    font-weight: 400;
    letter-spacing: 0.15px;
  }

  &__anchor.is-active &__anchor-text {
    font-size: 18px;
    line-height: 24px;
    font-weight: 600;
  }

  &__main {
    position: relative;
    display: flex;
    flex-direction: column;
    min-width: 0;
    height: 100%;
    box-sizing: border-box;
    min-height: 0;
  }

  &__close {
    position: absolute;
    top: 32px;
    right: 0;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 50%;
    background: #e5e7e8;
    color: $text-subtitle-color;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 20px;

    &:hover {
      color: $text-title-color;
    }
  }

  &__content {
    flex: 1;
    min-height: 0;
    max-height: none;
    overflow-y: auto;
    overscroll-behavior: none;
    padding-top: 56px;
    padding-bottom: 24px;
    padding-left: 32px;
    padding-right: 62px;
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__section + &__section {
    margin-top: 48px;
  }

  &__section-header {
    display: flex;
    align-items: center;
    gap: $spacing-md;
    min-height: 36px;
    margin-bottom: $spacing-xl;
  }

  &__section-title {
    margin: 0;
    color: $text-title-color;
    font-size: 18px;
    line-height: 28px;
    font-weight: 600;
  }

  &__view-all {
    display: inline-flex;
    align-items: center;
    min-height: 36px;
    padding: 0;
    border: none;
    background: transparent;
    color: $text-title-color;
    font-size: $font-size-card-content;
    line-height: $line-height-card-content;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover,
    &:focus-visible {
      color: $primary-color;
    }
  }

  &__view-all-icon {
    font-size: 14px;
    color: inherit;
  }

  &__cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
    justify-content: flex-start;

    @include respond-to-max('xxl') {
      grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    }
  }

  &__card {
    :deep(.product-list-card__model) {
      margin-bottom: 0;
    }

    :deep(.product-list-card__subtitle) {
      display: none;
    }

    :deep(.product-list-card__info) {
      margin-bottom: 16px;
    }
  }

  &__all-card {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-width: 0;
    min-height: 344px;
    padding: 16px;
    border: none;
    border-radius: 12px;
    background: $bg-white;
    box-shadow: none;
    color: $text-title-color;
    cursor: pointer;
    transition: box-shadow 0.2s ease;

    &:hover {
      background: $bg-white;
      box-shadow: 0 4px 10px rgba($color-black, 0.1);
    }
  }

  &__all-card-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  &__all-card-text {
    font-size: 16px;
    line-height: 22px;
    font-weight: 400;
  }

  &__all-card-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: $text-title-color;
    color: $bg-white;
  }

  &__all-card-icon {
    font-size: 14px;
    color: inherit;
  }

  &__skeleton {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    padding-top: 24px;
  }

  &__skeleton-card {
    height: 344px;
    border-radius: 12px;
    background: linear-gradient(
      90deg,
      rgba($bg-white, 0.8) 0%,
      rgba($bg-white, 1) 50%,
      rgba($bg-white, 0.8) 100%
    );
    background-size: 200% 100%;
    animation: megaMenuSkeleton 1.2s ease-in-out infinite;
  }
}

@media (prefers-reduced-motion: reduce) {
  .category-mega-menu-drop-enter-active,
  .category-mega-menu-drop-leave-active,
  .category-mega-menu-drop-enter-active::after,
  .category-mega-menu-drop-leave-active::after,
  .category-mega-menu-drop-enter-active .category-mega-menu,
  .category-mega-menu-drop-leave-active .category-mega-menu,
  .category-mega-menu-drop-enter-active .category-mega-menu__inner,
  .category-mega-menu-drop-leave-active .category-mega-menu__inner {
    transition-duration: 0.01ms;
    transition-delay: 0ms;
  }
}

.mobile-overlay {
  position: fixed;
  top: $base-header-height;
  left: 0;
  right: 0;
  bottom: 0;
  background: color.change($text-title-color, $alpha: 0.5);
  z-index: 999;
  backdrop-filter: blur(2px);

  @include respond-to-max('lg') {
    .base-header & {
      top: $base-header-height-mobile;
      background: transparent;
      backdrop-filter: none;
    }
  }
}

.mobile-menu {
  position: absolute;
  top: $base-header-height;
  left: 0;
  right: 0;
  background: $bg-white;
  overflow-y: auto;
  z-index: 1000;
  height: calc(100dvh - #{$base-header-height});

  @include respond-to-max('lg') {
    .base-header & {
      top: $base-header-height-mobile;
      background: $bg-color;
      height: calc(100dvh - #{$base-header-height-mobile});
    }
  }

  /* Hide the scrollbar while keeping scrolling enabled */
  -ms-overflow-style: none;
  /* IE and Edge */
  scrollbar-width: none;
  /* Firefox */

  &::-webkit-scrollbar {
    display: none;
    /* Chrome, Safari and Opera */
  }

  &__content {
    padding: 0;
    display: flex;
    flex-direction: column;
  }

  &__link {
    font-size: 16px;
    height: 48px;
    display: flex;
    align-items: center;
    padding: 0 20px;
    color: $text-title-color;
    text-decoration: none;
    transition: all 0.2s;

    &--active {
      color: $text-title-color;
      font-weight: 400;
    }

    &:hover {
      color: $primary-color;
    }

    &--skeleton {
      width: auto;
      height: 48px;
      margin: 0;
      padding: 0 20px;
      pointer-events: none;

      :deep(.arco-skeleton) {
        width: auto;
      }

      :deep(.arco-skeleton-line-row) {
        height: 20px;
        border-radius: 999px;
        margin: 0;
      }
    }

    @include respond-to-max('lg') {
      width: 100%;
      height: 44px;
      padding: 0 16px;
      background: $bg-white;
      color: $text-title-color;
      font-size: 14px;
      line-height: 20px;
      box-sizing: border-box;

      &--active,
      &:hover {
        color: $text-title-color;
      }

      &--skeleton {
        height: 44px;
        padding: 0 16px;
      }
    }
  }

  &__group,
  &__row,
  &__row-link,
  &__toggle,
  &__sublist,
  &__subitem {
    @include respond-to-max('lg') {
      box-sizing: border-box;
    }
  }

  &__group {
    @include respond-to-max('lg') {
      width: 100%;
      background: $bg-white;
    }
  }

  &__row {
    @include respond-to-max('lg') {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
      height: 44px;
      padding: 0 16px;
      background: $bg-white;

      &::after {
        content: '';
        position: absolute;
        left: 16px;
        right: 16px;
        bottom: 0;
        height: 1px;
        background: $border-light-color;
      }
    }
  }

  &__row-link {
    @include respond-to-max('lg') {
      display: flex;
      align-items: center;
      align-self: stretch;
      flex: 1 1 auto;
      min-width: 0;
      color: $text-title-color;
      font-size: 14px;
      line-height: 20px;
      text-decoration: none;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &:hover,
      &--active {
        color: $text-title-color;
      }
    }
  }

  &__toggle {
    @include respond-to-max('lg') {
      position: relative;
      z-index: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      width: 16px;
      height: 16px;
      margin: 0;
      padding: 0;
      border: 0;
      background: transparent;
      color: $text-subtitle-color;
      cursor: pointer;
    }
  }

  &__toggle-icon {
    @include respond-to-max('lg') {
      width: 16px;
      height: 16px;
      color: inherit;
    }
  }

  &__sublist {
    @include respond-to-max('lg') {
      display: flex;
      flex-direction: column;
      background: $bg-white;
    }
  }

  &__subitem {
    @include respond-to-max('lg') {
      display: flex;
      align-items: center;
      width: 100%;
      height: 38px;
      padding: 0 16px 0 28px;
      background: $bg-white;
      color: $text-content-color;
      font-size: 12px;
      line-height: 18px;
      font-weight: 400;
      text-decoration: none;

      &:hover {
        color: $text-content-color;
      }
    }
  }

  /* Category styles */
  &__categories {
    display: flex;
    flex-direction: column;
  }

  &__category {
    overflow: hidden;
  }

  &__category-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 20px;
    font-size: 16px;
    color: $text-title-color;
    cursor: pointer;
    background-color: $bg-white;
    text-decoration: none;

    &--link {
      cursor: pointer;
      text-decoration: none;
      color: $text-title-color;

      &:hover {
        color: $text-title-color;
      }
    }
  }

  &__category-name {
    font-weight: 400;
  }

  &__category-toggle {
    font-size: 12px;
    transition: transform 0.2s;
    color: $text-title-color;

    &--expanded {
      transform: rotate(180deg);
    }
  }

  &__category-children {
    display: flex;
    flex-direction: column;
    background-color: $bg-white;
    transition: all 0.3s ease;
  }

  &__category-child {
    padding: 7px 20px 7px 40px;
    font-size: 14px;
    color: $text-title-color;
    text-decoration: none;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Cart button wrapper */
.cart-button-wrapper {
  position: relative;
  display: inline-block;
}

.cart-panel-wrapper {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1001;
}

.cart-panel-bridge {
  position: absolute;
  top: 0;
  right: 0;
  width: 100%;
  height: 2px;
  background: transparent;
  pointer-events: auto;
}

.cart-panel {
  position: relative;
  margin-top: 2px;
  overflow: visible;
  animation: fadeIn 0.2s ease-out;
}

/* User button wrapper */
.user-button-wrapper {
  position: relative;
  display: inline-block;
}

/* User panel wrapper */
.user-panel-wrapper {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1001;
}

.user-panel-wrapper--member {
  width: 173px;
}

/* User panel bridge */
.user-panel-bridge {
  position: absolute;
  top: 0; // Start from the wrapper top without extending upward.
  right: 0;
  width: 100%; // Match the wrapper width.
  height: 2px; // Fill the gap created by the panel top margin.
  background: transparent;
  pointer-events: auto; // Accept pointer events across the bridge area.
}

/* User panel */
.user-panel {
  position: relative;
  margin-top: 2px; // Keep the gap small so hover transitions stay stable.
  box-sizing: border-box;
  background-color: $bg-white;
  border: 1px solid $border-dashed-color;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  animation: fadeIn 0.2s ease-out;
}

.user-panel--member {
  width: 173px;
  height: 230px;
  border-color: $border-light-color;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.user-panel--guest {
  height: auto;
}

/* Guest panel */
.user-panel__guest {
  padding: 20px;
  width: 280px;

  &-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .arco-btn {
      height: 40px;
      font-size: 14px;
      font-weight: 500;
    }

    .arco-btn-primary {
      background-color: $primary-color;
      border-color: $primary-color;
    }

    .arco-btn-default {
      background-color: $bg-color;
      border: none;
    }
  }
}

/* Logged-in panel */
.user-panel__member {
  width: 100%;
  height: 100%;
  padding: 3px 0;
  box-sizing: border-box;

  &-profile {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    height: 114px;
    padding: 12px 3px;
    box-sizing: border-box;
  }

  &-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    width: 166px;
    min-width: 0;
    text-align: center;
  }

  &-name,
  &-phone {
    width: 100%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    line-height: 20px;
  }

  &-name {
    color: $text-title-color;
    font-weight: 500;
  }

  &-phone {
    color: $text-subtitle-color;
    font-weight: 400;
  }

  &-menu {
    &-item {
      display: flex;
      align-items: center;
      gap: 20px;
      width: 100%;
      height: 36px;
      padding: 7px 12px;
      box-sizing: border-box;
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover {
        background-color: #f7f8fa;
      }

      span {
        display: block;
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 14px;
        line-height: 20px;
        font-weight: 400;
        color: $text-title-color;
      }
    }

    &-arrow {
      flex: 0 0 auto;
      color: $text-subtitle-color;
      font-size: 14px;
    }
  }
}

.user-panel__avatar {
  width: 40px;
  height: 40px;
  overflow: hidden;
  border-radius: 6px;
  flex-shrink: 0;
  background: $bg-light-gray;

  :deep(.base-image),
  :deep(.base-image__optimized),
  :deep(.base-image__inner),
  :deep(.arco-image),
  :deep(.arco-image-img) {
    width: 100%;
    height: 100%;
  }

  :deep(.base-image__inner),
  :deep(.arco-image-img) {
    display: block;
    object-fit: cover;
  }

  :deep(.base-image__placeholder),
  :deep(.base-image__error) {
    display: none;
  }
}

/* Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes megaMenuSkeleton {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

/* Language dropdown overrides */
.language-dropdown {
  &__panel {
    width: 300px;
    background-color: $bg-white;
    border-radius: 8px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }
}
</style>
